import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { toast } from 'react-toastify';
import {
  ArrowLeft, ScrollText, BarChart3, Coins, Scale, Settings, Plus, Search,
  Pencil, Trash2, BookOpen, Calculator, CircleDot, Lightbulb, X, MoreVertical,
  Banknote, CreditCard, Building2, ClipboardList, Wallet, DollarSign, User, Clock,
  TrendingUp, Calendar, AlertTriangle, Printer
} from 'lucide-react';
import LogModal from '../components/LogModal';
import PriceInput from '../components/PriceInput';
import logService from '../services/logService';
import { API_URL } from '../config/api';
import { handlePrint } from '../utils/printHelper';
import { normalizePhoneDigits, formatPhoneDisplay, isPhoneComplete } from '../utils/phoneFormat';
import './ScrapPurchase.css';
import ScrapMobileHome from './ScrapMobileHome';
import './RetailSales.css';
import useEscapeClose from '../hooks/useEscapeClose';

// mobileApp: iOS uygulamasının WebView'ı (/mobile/hurda, MobileHurda.js) — ana görünüm telefona göre
// ScrapMobileHome ile çizilir; admin mağazayı sayfanın içinden seçer (web'de üst çubuktaki seçim yok).
// Modallar, hesaplar ve kayıt akışı web ile ortaktır; tarayıcı yazdırması telefonda sorulmaz.
const ScrapPurchase = ({ user, selectedStore, mobileApp = false }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const fromRetailSales = location.state?.from === 'retail-sales';
  const openScrapId = location.state?.openScrapId || (() => {
    const params = new URLSearchParams(location.search);
    const scrapParam = params.get('scrap');
    return scrapParam ? parseInt(scrapParam) : null;
  })();

  // Mağaza türünü belirle (store_id 1 = altın, diğerleri = gümüş)
  const [mobileStoreId, setMobileStoreId] = useState(() => user?.store_id || 1);
  const currentStoreId = mobileApp && user?.role === 'admin'
    ? mobileStoreId
    : user?.role === 'admin' ? (selectedStore?.id || user?.store_id) : user?.store_id;
  const isGoldStore = currentStoreId === 1;
  const storeMetalType = isGoldStore ? 'gold' : 'silver';
  const [purchases, setPurchases] = useState([]);
  const [customers, setCustomers] = useState([]);
  const [salesStaff, setSalesStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState('all');
  const [showModal, setShowModal] = useState(false);
  const [editingPurchase, setEditingPurchase] = useState(null);
  const [originalPurchase, setOriginalPurchase] = useState(null);
  const [currentTime, setCurrentTime] = useState(new Date());
  const [prices, setPrices] = useState(null);
  const [showLogModal, setShowLogModal] = useState(false);

  // Grup detay modalı
  const [showDetailModal, setShowDetailModal] = useState(false);
  const [selectedGroupDetail, setSelectedGroupDetail] = useState(null);
  const [groupDetailItems, setGroupDetailItems] = useState([]);

  // Yardım modal için
  const [showHelpModal, setShowHelpModal] = useState(false);

  // Modal kapanış animasyonu için
  const [closingModal, setClosingModal] = useState(null);

  // Genel onay modalı (window.confirm yerine)
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [confirmModalConfig, setConfirmModalConfig] = useState({
    title: '',
    message: '',
    confirmText: 'Evet',
    cancelText: 'İptal',
    type: 'warning',
    onConfirm: () => {}
  });

  // Genel onay modal'ı gösterme fonksiyonu
  const showConfirmation = (config) => {
    setConfirmModalConfig({
      title: config.title || 'Onay',
      message: config.message || '',
      confirmText: config.confirmText || 'Evet',
      cancelText: config.cancelText || 'İptal',
      onConfirm: config.onConfirm || (() => {}),
      type: config.type || 'warning'
    });
    setShowConfirmModal(true);
  };

  // Özel Kur Modalı - localStorage ile senkronize
  const [showRateModal, setShowRateModal] = useState(false);
  const [customRates, setCustomRates] = useState(() => {
    const saved = localStorage.getItem('sharedCustomRates');
    return saved ? JSON.parse(saved) : {};
  });
  const [rateModalCurrency, setRateModalCurrency] = useState('GOLD');
  const [rateModalValue, setRateModalValue] = useState('');
  const [rateModalAlisValue, setRateModalAlisValue] = useState('');
  const [rateModalSatisValue, setRateModalSatisValue] = useState('');
  const [rateModalAlisPercentage, setRateModalAlisPercentage] = useState('');
  const [rateModalSatisPercentage, setRateModalSatisPercentage] = useState('');
  const [rateModalInputMode, setRateModalInputMode] = useState('manual');
  const [rateModalAlisMode, setRateModalAlisMode] = useState('manual');
  const [rateModalSatisMode, setRateModalSatisMode] = useState('manual');

  // Parite görünürlük ayarları
  const [showRatesMenu, setShowRatesMenu] = useState(false);
  const [visibleRates, setVisibleRates] = useState(() => {
    const saved = localStorage.getItem('scrapPurchaseVisibleRates');
    return saved ? JSON.parse(saved) : {
      gold: true, silver: true, usd: true, eur: true, gbp: true, silverUsd: true
    };
  });

  // Sütun görünürlüğü ayarları
  const [showColumnSettings, setShowColumnSettings] = useState(false);
  const defaultColumns = {
    type: true,
    customer: true,
    purity: true,
    pureWeight: true,
    unitPrice: true,
    total: true,
    paymentMethod: true,
    staff: false,
    date: true,
    notes: false
  };
  const [visibleColumns, setVisibleColumns] = useState(() => {
    const saved = localStorage.getItem('scrapPurchaseVisibleColumns');
    return saved ? JSON.parse(saved) : defaultColumns;
  });

  // Tüm sütun tanımları
  const allColumns = [
    { key: 'type', label: 'Metal Türü' },
    { key: 'customer', label: 'Müşteri' },
    { key: 'purity', label: 'Ayar / Brüt' },
    { key: 'pureWeight', label: 'Has Gram' },
    { key: 'unitPrice', label: 'Birim Fiyat' },
    { key: 'total', label: 'Toplam' },
    { key: 'paymentMethod', label: 'Ödeme' },
    { key: 'staff', label: 'Personel' },
    { key: 'date', label: 'Tarih' },
    { key: 'notes', label: 'Notlar' }
  ];

  // Sütun görünürlüğü değiştir
  const toggleColumn = (columnKey) => {
    const newColumns = { ...visibleColumns, [columnKey]: !visibleColumns[columnKey] };
    setVisibleColumns(newColumns);
    localStorage.setItem('scrapPurchaseVisibleColumns', JSON.stringify(newColumns));
  };

  // Tüm sütunları sıfırla
  const resetColumns = () => {
    setVisibleColumns(defaultColumns);
    localStorage.setItem('scrapPurchaseVisibleColumns', JSON.stringify(defaultColumns));
  };

  // Müşteri seçim/oluşturma
  const [customerMode, setCustomerMode] = useState('select'); // 'select' veya 'new'
  const [customerSearch, setCustomerSearch] = useState('');
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Kayıtsız devam et: görünür sekme yok, müşteri ekranı görse fark etmesin diye
  // "Yeni Hurda Alımı" başlığına ~1.2sn basılı tutunca tetiklenen gizli kısayol.
  const anonymousScrapHoldTimer = useRef(null);
  const startAnonymousScrapHold = () => {
    if (anonymousScrapHoldTimer.current) return;
    anonymousScrapHoldTimer.current = setTimeout(() => {
      anonymousScrapHoldTimer.current = null;
      setCustomerMode('anonymous');
      setFormData(prev => ({ ...prev, customer_id: null, customer_name: 'Kayıtsız Müşteri', customer_phone: '' }));
      setCustomerSearch('');
      toast.info('Kayıtsız müşteri olarak devam ediliyor');
    }, 1200);
  };
  const cancelAnonymousScrapHold = () => {
    if (anonymousScrapHoldTimer.current) {
      clearTimeout(anonymousScrapHoldTimer.current);
      anonymousScrapHoldTimer.current = null;
    }
  };

  const [formData, setFormData] = useState({
    customer_id: '',
    customer_name: '',
    customer_phone: '',
    staff_id: '',
    staff_name: '',
    metal_type: storeMetalType,
    purity: isGoldStore ? '22' : '925',
    custom_purity_rate: null, // Tek seferlik özel oran (null ise varsayılan kullanılır)
    gross_weight: '',
    waste_percent: '',
    waste_gram: '',
    unit_price: '',
    bonus_percent: '',
    notes: '',
    created_at: '' // Düzenleme modunda tarih/saat
  });

  // Ödeme modalı için state'ler
  const [showPaymentModal, setShowPaymentModal] = useState(false);
  const [paymentAmount, setPaymentAmount] = useState('');
  const [selectedPaymentCurrency, setSelectedPaymentCurrency] = useState('TRY');
  const [scrapPayments, setScrapPayments] = useState([]);
  const [showPaymentCreditOptions, setShowPaymentCreditOptions] = useState(false);
  const [pendingScrapData, setPendingScrapData] = useState(null);
  const [bankAccounts, setBankAccounts] = useState([]);
  const [selectedBankAccount, setSelectedBankAccount] = useState(null);
  const [showBankModal, setShowBankModal] = useState(false);
  const [pendingPaymentMethod, setPendingPaymentMethod] = useState(null);

  // Manuel toplam düzenleme için
  const [customTotal, setCustomTotal] = useState(null);
  const [isEditingTotal, setIsEditingTotal] = useState(false);
  const [editingTotalValue, setEditingTotalValue] = useState('');

  // Telefon numarası eşleşen müşteri
  const [matchingCustomer, setMatchingCustomer] = useState(null);

  // Ayar katsayıları - varsayılan değerler
  const defaultPurityRates = {
    gold: {
      '24': 0.985,
      '22': 0.900,
      '21': 0.800,
      '18': 0.700,
      '14': 0.500,
      '10': 0.350,
      '9': 0.300,
      '8': 0.250
    },
    silver: {
      '999': 0.750,
      '925': 0.650,
      '900': 0.600,
      '850': 0.550,
      '800': 0.500,
      '750': 0.450,
      '700': 0.400
    }
  };

  // Varsayılan saflık oranlarını kullan
  const [purityRates, setPurityRates] = useState(defaultPurityRates);

  // Ayar oranı düzenleme için
  const [isEditingPurityRate, setIsEditingPurityRate] = useState(false);
  const [editingPurityRateValue, setEditingPurityRateValue] = useState('');

  // ESC tuşu ile modal kapatma
  useEscapeClose([
    { state: showConfirmModal, setter: setShowConfirmModal },
    { state: showHelpModal, setter: setShowHelpModal },
    { state: showDetailModal, setter: setShowDetailModal },
    { state: showPaymentModal, setter: setShowPaymentModal },
    { state: showBankModal, setter: setShowBankModal },
    { state: showRateModal, setter: setShowRateModal },
    { state: showModal, setter: setShowModal },
    { state: showLogModal, setter: () => {} },
  ]);

  // Ayar oranını güncelle (tek seferlik - localStorage'a kaydetmez)
  const updatePurityRate = (metalType, purity, newRate) => {
    const rate = parseFloat(newRate) / 1000; // 985 -> 0.985
    if (isNaN(rate) || rate < 0 || rate > 1) return;

    // Sadece formData'daki custom_purity_rate'i güncelle (kalıcı değil)
    setFormData(prev => ({ ...prev, custom_purity_rate: rate }));
  };

  // Liste/kart görünümünde "Ayar" yanında milyem göstermek için: tek bir ayar ve tek bir
  // metal türü belirliyse (gruplu satırlarda birden fazla ayar/metal karışmışsa purities/
  // metal_types virgüllü gelir — o durumda tek milyem anlamlı olmadığı için null döner).
  const getMilyemForPurchase = (purchase) => {
    const purityRaw = purchase.purities || purchase.purity;
    if (!purityRaw || String(purityRaw).includes(',')) return null;
    const metalTypeRaw = purchase.metal_types || purchase.metal_type;
    if (!metalTypeRaw || String(metalTypeRaw).includes(',')) return null;
    const rate = purityRates[metalTypeRaw]?.[purityRaw] ?? defaultPurityRates[metalTypeRaw]?.[purityRaw];
    if (!rate) return null;
    return Math.round(rate * 1000);
  };

  // Modal kapanış handler'ı (callback opsiyonel)
  const handleCloseModal = (modalName, setShowModal, callback) => {
    setClosingModal(modalName);
    setTimeout(() => {
      setShowModal(false);
      setClosingModal(null);
      if (callback) callback();
    }, 250);
  };

  // customRates değiştiğinde localStorage'a kaydet
  useEffect(() => {
    if (Object.keys(customRates).length > 0) {
      localStorage.setItem('sharedCustomRates', JSON.stringify(customRates));
    }
  }, [customRates]);

  // Sayfa odaklandığında localStorage'dan customRates'i güncelle
  useEffect(() => {
    const handleFocus = () => {
      const saved = localStorage.getItem('sharedCustomRates');
      if (saved) {
        const parsed = JSON.parse(saved);
        setCustomRates(prev => {
          if (JSON.stringify(prev) !== JSON.stringify(parsed)) {
            return parsed;
          }
          return prev;
        });
      }
    };
    window.addEventListener('focus', handleFocus);
    return () => window.removeEventListener('focus', handleFocus);
  }, []);

  useEffect(() => {
    fetchCustomers();
    fetchSalesStaff();
    fetchPrices();
    fetchBankAccounts();

    // Saat güncelleme
    const timer = setInterval(() => setCurrentTime(new Date()), 1000);
    return () => clearInterval(timer);
  }, []);

  // Mağaza değişince hurdaları ve personelleri yeniden yükle
  useEffect(() => {
    if (currentStoreId) {
      fetchPurchases();
      fetchSalesStaff();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentStoreId]);

  // Perakende satıştan openScrapId ile gelindiyse ilgili fişi otomatik aç
  useEffect(() => {
    if (openScrapId && purchases.length > 0 && !showDetailModal) {
      const targetGroup = purchases.find(p => p.first_id === openScrapId || p.items?.some(i => i.id === openScrapId));
      if (targetGroup) {
        openGroupDetail(targetGroup);
        // state ve URL'i temizle ki tekrar açılmasın
        window.history.replaceState({}, document.title, '/scrap-purchase');
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openScrapId, purchases]);

  const fetchPrices = async (showToast = false) => {
    try {
      const response = await fetch(`${API_URL}/prices`);
      const data = await response.json();
      if (data && !data.error) {
        setPrices({
          USD_alis: data.USD_alis || data.USD,
          USD_satis: data.USD_satis || data.USD,
          EUR_alis: data.EUR_alis || data.EUR,
          EUR_satis: data.EUR_satis || data.EUR,
          altin_alis: data.altin_alis,
          altin_satis: data.altin_satis || data.altin,
          gumus_alis: data.gumus_alis,
          gumus_satis: data.gumus_satis || data.gumus
        });
        if (showToast) toast.success('Fiyatlar güncellendi!');
      } else {
        if (showToast) toast.error('Fiyatlar alınamadı!');
      }
    } catch (error) {
      console.error('Fiyat çekme hatası:', error);
      if (showToast) toast.error('Fiyat servisi bağlantı hatası!');
    }
  };

  const fetchSalesStaff = async () => {
    try {
      const response = await fetch(`${API_URL}/sales-staff${currentStoreId ? `?store_id=${currentStoreId}` : ''}`);
      const data = await response.json();
      setSalesStaff(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Satış personeli yükleme hatası:', error);
    }
  };

  const fetchBankAccounts = async () => {
    try {
      const response = await fetch(`${API_URL}/bank-accounts?active_only=true`);
      const data = await response.json();
      if (data.success && Array.isArray(data.accounts)) {
        setBankAccounts(data.accounts);
      } else {
        setBankAccounts([]);
      }
    } catch (error) {
      console.error('Banka hesapları yükleme hatası:', error);
      setBankAccounts([]);
    }
  };

  const fetchPurchases = async () => {
    try {
      setLoading(true);
      // Gruplu endpoint kullan
      const response = await fetch(`${API_URL}/scrap-purchases/grouped?store_id=${currentStoreId}`);
      const data = await response.json();
      setPurchases(Array.isArray(data) ? data : []);
    } catch (error) {
      console.error('Hurda alımları yükleme hatası:', error);
      setPurchases([]);
    } finally {
      setLoading(false);
    }
  };

  // Grup detaylarını getir ve modalı aç
  const openGroupDetail = async (group) => {
    try {
      setSelectedGroupDetail(group);
      const response = await fetch(`${API_URL}/scrap-purchases/by-group/${group.group_id}`);
      const data = await response.json();
      setGroupDetailItems(Array.isArray(data) ? data : []);
      setShowDetailModal(true);
    } catch (error) {
      console.error('Grup detayları yükleme hatası:', error);
      toast.error('Detaylar yüklenemedi!');
    }
  };

  const fetchCustomers = async () => {
    try {
      // Tüm müşterileri çek (limit yüksek tutuldu)
      const response = await fetch(`${API_URL}/customers?limit=5000`);
      const data = await response.json();
      // API {customers: [...]} formatında dönüyor
      const customerList = data.customers || data;
      setCustomers(Array.isArray(customerList) ? customerList : []);
    } catch (error) {
      console.error('Müşteri yükleme hatası:', error);
      setCustomers([]);
    }
  };

  // Müşteri arama fonksiyonu (backend'den arama)
  const searchCustomers = async (query) => {
    if (!query || query.length < 2) return;
    try {
      const storeType = isGoldStore ? 'Altın' : 'Gümüş';
      const response = await fetch(`${API_URL}/customers/search/${encodeURIComponent(query)}?storeType=${storeType}`);
      const data = await response.json();
      if (Array.isArray(data)) {
        // Arama sonuçlarını mevcut listeye ekle (duplicate olmayanları)
        setCustomers(prev => {
          const existingIds = new Set(prev.map(c => c.id));
          const newCustomers = data.filter(c => !existingIds.has(c.id));
          return [...prev, ...newCustomers];
        });
      }
    } catch (error) {
      console.error('Müşteri arama hatası:', error);
    }
  };

  // Özel kurları getir
  const fetchCustomRates = async () => {
    try {
      const response = await fetch(`${API_URL}/custom-rates`);
      const data = await response.json();
      const ratesObj = {};
      data.forEach(rate => {
        ratesObj[rate.currency] = rate;
      });
      setCustomRates(ratesObj);
    } catch (error) {
      console.error('Özel kur getirme hatası:', error);
    }
  };

  useEffect(() => {
    fetchCustomRates();
  }, []);

  // Özel kur kaydet
  const saveRateModal = async () => {
    // Alış kontrolü
    const hasAlisValue = rateModalAlisMode === 'percentage' ? !!rateModalAlisPercentage : !!rateModalAlisValue;
    // Satış kontrolü
    const hasSatisValue = rateModalSatisMode === 'percentage' ? !!rateModalSatisPercentage : !!rateModalSatisValue;

    if (!hasAlisValue && !hasSatisValue) {
      toast.error('En az bir kur değeri giriniz');
      return;
    }

    try {
      const payload = { currency: rateModalCurrency };

      // Alış: moduna göre kaydet
      if (rateModalAlisMode === 'percentage') {
        payload.rate_alis = null;
        payload.alis_percentage = rateModalAlisPercentage ? parseFloat(rateModalAlisPercentage) : null;
      } else {
        payload.rate_alis = rateModalAlisValue ? parseFloat(rateModalAlisValue) : null;
        payload.alis_percentage = null;
      }

      // Satış: moduna göre kaydet
      if (rateModalSatisMode === 'percentage') {
        payload.rate_satis = null;
        payload.satis_percentage = rateModalSatisPercentage ? parseFloat(rateModalSatisPercentage) : null;
      } else {
        payload.rate_satis = rateModalSatisValue ? parseFloat(rateModalSatisValue) : null;
        payload.satis_percentage = null;
      }

      const response = await fetch(`${API_URL}/custom-rates`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        toast.success(`${getRateModalLabel(rateModalCurrency)} kurları kaydedildi`);
        fetchCustomRates();
        setShowRateModal(false);
        setRateModalValue('');
        setRateModalAlisValue('');
        setRateModalSatisValue('');
        setRateModalAlisPercentage('');
        setRateModalSatisPercentage('');
      } else {
        toast.error('Kur kaydedilemedi');
      }
    } catch (error) {
      console.error('Kur kaydetme hatası:', error);
      toast.error('Kur kaydedilemedi');
    }
  };

  // Özel kuru kaldır
  const removeCustomRate = async (currency) => {
    try {
      const response = await fetch(`${API_URL}/custom-rates/${currency}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        toast.success(`${getRateModalLabel(currency)} özel kuru kaldırıldı`);
        fetchCustomRates();
        setShowRateModal(false);
      }
    } catch (error) {
      console.error('Kur silme hatası:', error);
    }
  };

  // Parite görünürlüğünü toggle et
  const toggleRateVisibility = (rate) => {
    const newVisibleRates = { ...visibleRates, [rate]: !visibleRates[rate] };
    setVisibleRates(newVisibleRates);
    localStorage.setItem('scrapPurchaseVisibleRates', JSON.stringify(newVisibleRates));
  };

  // Kur modalını aç
  const openRateModal = (currency) => {
    setRateModalCurrency(currency);

    if (customRates[currency]) {
      setRateModalAlisValue(customRates[currency].rate_alis?.toString() || '');
      setRateModalSatisValue(customRates[currency].rate_satis?.toString() || customRates[currency].rate?.toString() || '');
      const alisPerc = customRates[currency].alis_percentage;
      const satisPerc = customRates[currency].satis_percentage;
      setRateModalAlisPercentage(alisPerc ? alisPerc.toString() : '');
      setRateModalSatisPercentage(satisPerc ? satisPerc.toString() : '');
      // Bağımsız modları belirle
      setRateModalAlisMode(alisPerc ? 'percentage' : 'manual');
      setRateModalSatisMode(satisPerc ? 'percentage' : 'manual');
      setRateModalInputMode((alisPerc || satisPerc) ? 'percentage' : 'manual');
    } else if (prices) {
      setRateModalAlisPercentage('');
      setRateModalSatisPercentage('');
      setRateModalAlisMode('manual');
      setRateModalSatisMode('manual');
      setRateModalInputMode('manual');
      let alisKey, satisKey;
      if (currency === 'GOLD') {
        alisKey = 'altin_alis';
        satisKey = 'altin_satis';
      } else if (currency === 'SILVER') {
        alisKey = 'gumus_alis';
        satisKey = 'gumus_satis';
      } else {
        alisKey = `${currency}_alis`;
        satisKey = `${currency}_satis`;
      }
      setRateModalAlisValue(prices[alisKey] || '');
      setRateModalSatisValue(prices[satisKey] || '');
    } else {
      setRateModalAlisValue('');
      setRateModalSatisValue('');
      setRateModalAlisPercentage('');
      setRateModalSatisPercentage('');
      setRateModalAlisMode('manual');
      setRateModalSatisMode('manual');
      setRateModalInputMode('manual');
    }
    setShowRateModal(true);
  };

  // Anlık piyasa kurlarını al
  const getBaseRates = () => {
    let baseAlis, baseSatis;
    if (rateModalCurrency === 'GOLD') {
      baseAlis = parseFloat(prices?.altin_alis) || 0;
      baseSatis = parseFloat(prices?.altin_satis) || 0;
    } else if (rateModalCurrency === 'SILVER') {
      baseAlis = parseFloat(prices?.gumus_alis) || 0;
      baseSatis = parseFloat(prices?.gumus_satis) || 0;
    } else if (rateModalCurrency === 'SILVER_USD') {
      const gumusAlis = parseFloat(prices?.gumus_alis) || 0;
      const gumusSatis = parseFloat(prices?.gumus_satis) || 0;
      const usdAlis = parseFloat(prices?.USD_alis) || 1;
      const usdSatis = parseFloat(prices?.USD_satis) || 1;
      baseAlis = gumusAlis / usdAlis;
      baseSatis = gumusSatis / usdSatis;
    } else {
      baseAlis = parseFloat(prices?.[`${rateModalCurrency}_alis`]) || 0;
      baseSatis = parseFloat(prices?.[`${rateModalCurrency}_satis`]) || 0;
    }
    return { baseAlis, baseSatis };
  };

  // Alış makas farkı değiştiğinde
  const handleAlisPercentageChange = (percentage) => {
    setRateModalAlisPercentage(percentage);
    if (!percentage || isNaN(parseFloat(percentage))) {
      return;
    }
    const pct = parseFloat(percentage);
    const { baseAlis } = getBaseRates();
    const newAlis = baseAlis * (1 - pct / 100);
    setRateModalAlisValue(newAlis.toFixed(2));
  };

  // Satış makas farkı değiştiğinde
  const handleSatisPercentageChange = (percentage) => {
    setRateModalSatisPercentage(percentage);
    if (!percentage || isNaN(parseFloat(percentage))) {
      return;
    }
    const pct = parseFloat(percentage);
    const { baseSatis } = getBaseRates();
    const newSatis = baseSatis * (1 + pct / 100);
    setRateModalSatisValue(newSatis.toFixed(2));
  };

  // Kur modalı için currency label al
  const getRateModalLabel = (currency) => {
    const labels = {
      'USD': 'Dolar',
      'EUR': 'Euro',
      'GBP': 'Sterlin',
      'GOLD': 'Altın',
      'SILVER': 'Gümüş',
      'SILVER_USD': 'Gümüş/USD Paritesi'
    };
    return labels[currency] || currency;
  };

  // Kur modalı için price keys al
  const getRatePriceKeys = (currency) => {
    const keys = {
      'USD': { alis: 'USD_alis', satis: 'USD_satis' },
      'EUR': { alis: 'EUR_alis', satis: 'EUR_satis' },
      'GOLD': { alis: 'altin_alis', satis: 'altin_satis' },
      'SILVER': { alis: 'gumus_alis', satis: 'gumus_satis' }
    };
    return keys[currency] || { alis: `${currency}_alis`, satis: `${currency}_satis` };
  };

  // Dinamik alış kuru hesaplama - makas farkı yüzdesinden veya sabit kurdan
  const getAlisRate = (currency) => {
    const customRate = customRates[currency];
    let liveAlis;
    if (currency === 'GOLD') {
      liveAlis = parseFloat(prices?.altin_alis) || 0;
    } else if (currency === 'SILVER') {
      liveAlis = parseFloat(prices?.gumus_alis) || 0;
    } else {
      liveAlis = parseFloat(prices?.[`${currency}_alis`]) || parseFloat(prices?.[currency]) || 0;
    }

    // Önce makas farkı yüzdesini kontrol et (dinamik hesaplama)
    if (customRate?.alis_percentage != null && customRate.alis_percentage !== '' && !isNaN(customRate.alis_percentage)) {
      return liveAlis * (1 - parseFloat(customRate.alis_percentage) / 100);
    }
    // Sonra manuel sabit alış kurunu kontrol et
    if (customRate?.rate_alis != null && parseFloat(customRate.rate_alis) > 0) {
      return parseFloat(customRate.rate_alis);
    }
    // Manuel alış kuru yoksa anlık alış kurunu kullan
    return liveAlis;
  };

  // Dinamik satış kuru hesaplama - manuel satış kuru varsa onu kullan, yoksa anlık kur
  const getSatisRate = (currency) => {
    const customRate = customRates[currency];
    let liveSatis;
    if (currency === 'GOLD') {
      liveSatis = parseFloat(prices?.altin_satis) || 0;
    } else if (currency === 'SILVER') {
      liveSatis = parseFloat(prices?.gumus_satis) || 0;
    } else {
      liveSatis = parseFloat(prices?.[`${currency}_satis`]) || parseFloat(prices?.[currency]) || 0;
    }

    // Önce makas farkı yüzdesini kontrol et (dinamik hesaplama)
    if (customRate?.satis_percentage != null && customRate.satis_percentage !== '' && !isNaN(customRate.satis_percentage)) {
      return liveSatis * (1 + parseFloat(customRate.satis_percentage) / 100);
    }
    // Sonra manuel sabit satış kurunu kontrol et
    if (customRate?.rate_satis != null && parseFloat(customRate.rate_satis) > 0) {
      return parseFloat(customRate.rate_satis);
    }
    // Manuel satış kuru yoksa anlık satış kurunu kullan
    return liveSatis;
  };

  // Alış fiyatını al (hurda alımı için - manuel alış kuru varsa onu kullan, yoksa anlık)
  const getAlisPrice = (currency) => {
    const customRate = customRates[currency];
    // Önce makas farkı yüzdesini kontrol et
    if (customRate?.alis_percentage != null && customRate.alis_percentage !== '' && !isNaN(customRate.alis_percentage)) {
      const priceKey = getRatePriceKeys(currency).alis;
      const liveAlis = parseFloat(prices?.[priceKey]) || 0;
      return liveAlis * (1 - parseFloat(customRate.alis_percentage) / 100);
    }
    // Sonra manuel sabit alış kurunu kontrol et
    if (customRate?.rate_alis != null && parseFloat(customRate.rate_alis) > 0) {
      return parseFloat(customRate.rate_alis);
    }
    // Manuel alış kuru yoksa anlık alış kurunu kullan
    const priceKey = getRatePriceKeys(currency).alis;
    return parseFloat(prices?.[priceKey]) || 0;
  };

  // Fiyatlar veya özel kurlar yüklendiğinde birim fiyatı otomatik güncelle
  useEffect(() => {
    // Düzenleme modunda güncelleme
    if (editingPurchase) return;

    const currency = formData.metal_type === 'gold' ? 'GOLD' : 'SILVER';
    const price = getAlisPrice(currency);

    // Fiyat boşsa veya 0 ise güncelle
    if (price > 0 && (!formData.unit_price || formData.unit_price === '' || formData.unit_price === '0')) {
      setFormData(prev => ({ ...prev, unit_price: price.toFixed(2) }));
    }
  }, [prices, customRates, formData.metal_type, editingPurchase, formData.unit_price]);

  // Has gram hesaplama
  const calculatePureWeight = () => {
    const grossWeight = parseFloat(formData.gross_weight) || 0;
    const wasteGram = parseFloat(formData.waste_gram) || 0;
    // Önce custom_purity_rate'i kontrol et (tek seferlik), yoksa varsayılanı kullan
    const purityRate = formData.custom_purity_rate || purityRates[formData.metal_type]?.[formData.purity] || defaultPurityRates[formData.metal_type]?.[formData.purity] || 0;

    const netWeight = grossWeight - wasteGram;
    const pureWeight = netWeight * purityRate;

    return pureWeight.toFixed(2);
  };

  // Toplam tutar hesaplama
  const calculateTotal = () => {
    // Manuel değer varsa onu kullan
    if (customTotal !== null) {
      return customTotal.toFixed(2);
    }
    const pureWeight = parseFloat(calculatePureWeight()) || 0;
    const unitPrice = parseFloat(formData.unit_price) || 0;
    return (pureWeight * unitPrice).toFixed(2);
  };

  // Toplam düzenleme başlat
  const startEditingTotal = () => {
    const currentTotal = customTotal !== null ? customTotal : parseFloat(calculateTotal());
    setEditingTotalValue(currentTotal.toFixed(2));
    setIsEditingTotal(true);
  };

  // Toplam düzenleme bitir
  const finishEditingTotal = () => {
    const newValue = parseFloat(editingTotalValue);
    if (!isNaN(newValue) && newValue >= 0) {
      setCustomTotal(newValue);
    }
    setIsEditingTotal(false);
  };

  // Toplam düzenleme iptal
  const cancelEditingTotal = () => {
    setIsEditingTotal(false);
    setEditingTotalValue('');
  };

  // Metal türü değiştiğinde ayarı güncelle
  const handleMetalTypeChange = (type) => {
    const defaultPurity = type === 'gold' ? '22' : '925';
    const currency = type === 'gold' ? 'GOLD' : 'SILVER';
    const defaultPrice = getAlisPrice(currency);
    setFormData({
      ...formData,
      metal_type: type,
      purity: defaultPurity,
      unit_price: defaultPrice.toString()
    });
  };

  // Hızlı hurda seçimi (Çeyrek, Yarım, Tam)
  const quickScrapOptions = [
    { id: 'ceyrek', name: 'Çeyrek', weight: 1.75, purity: '22' },
    { id: 'yarim', name: 'Yarım', weight: 3.5, purity: '22' },
    { id: 'tam', name: 'Tam', weight: 7, purity: '22' }
  ];

  // Hurda Sepeti
  const [scrapCart, setScrapCart] = useState([]);

  // Hızlı hurda seçimi - form alanlarını doldur
  const handleQuickScrapSelect = (option) => {
    const goldPrice = getAlisPrice('GOLD');

    setFormData(prev => ({
      ...prev,
      metal_type: 'gold',
      purity: option.purity,
      custom_purity_rate: null,
      gross_weight: option.weight.toString(),
      unit_price: goldPrice.toFixed(2),
      notes: option.name
    }));
    toast.info(`${option.name} bilgileri forma dolduruldu`);
  };

  // Manuel hurda sepete ekleme
  const addScrapToCart = () => {
    const grossWeight = parseFloat(formData.gross_weight) || 0;
    const wasteGram = parseFloat(formData.waste_gram) || 0;
    const unitPrice = parseFloat(formData.unit_price) || 0;

    if (grossWeight <= 0) {
      toast.warning('Lütfen gram bilgisini girin');
      return;
    }

    const netWeight = grossWeight - wasteGram;
    const purityRate = formData.custom_purity_rate || purityRates[formData.metal_type]?.[formData.purity] || defaultPurityRates[formData.metal_type]?.[formData.purity] || 0;
    const pureWeight = netWeight * purityRate;
    const total = pureWeight * unitPrice;
    const metalName = formData.metal_type === 'gold' ? 'Altın' : 'Gümüş';

    const newScrap = {
      id: Date.now(),
      name: formData.notes || `${metalName} ${formData.purity} Ayar`,
      metal_type: formData.metal_type,
      purity: formData.purity,
      purity_rate: purityRate,
      gross_weight: grossWeight,
      waste_percent: parseFloat(formData.waste_percent) || 0,
      waste_gram: wasteGram,
      pure_weight: pureWeight,
      unit_price: unitPrice,
      unit_total: total,
      total: total,
      quantity: 1,
      bonus_percent: parseFloat(formData.bonus_percent) || 0,
      isQuickOption: false
    };

    setScrapCart([...scrapCart, newScrap]);
    toast.success('Hurda sepete eklendi');

    // Formu sıfırla (müşteri bilgisi hariç)
    setFormData({
      ...formData,
      gross_weight: '',
      waste_percent: '',
      waste_gram: '',
      bonus_percent: '',
      notes: ''
    });
  };

  // Hurda miktarını güncelle
  const updateScrapQuantity = (id, delta) => {
    setScrapCart(prev => prev.map(item => {
      if (item.id === id) {
        const newQty = Math.max(1, item.quantity + delta);
        return { ...item, quantity: newQty, total: item.unit_total * newQty };
      }
      return item;
    }));
  };

  // Hurda miktarını manuel ayarla
  const setScrapQuantity = (id, qty) => {
    const newQty = Math.max(1, parseInt(qty) || 1);
    setScrapCart(prev => prev.map(item => {
      if (item.id === id) {
        return { ...item, quantity: newQty, total: item.unit_total * newQty };
      }
      return item;
    }));
  };

  // Hurda sil
  const removeScrapFromCart = (id) => {
    setScrapCart(prev => prev.filter(item => item.id !== id));
  };

  // Hurda sepetini temizle
  const clearScrapCart = () => {
    setScrapCart([]);
    setCustomTotal(null);
    setIsEditingTotal(false);
    setEditingTotalValue('');
  };

  // Toplam hurda tutarı
  const calculatedScrapCartTotal = scrapCart.reduce((sum, item) => sum + item.total, 0);
  const scrapCartTotal = customTotal !== null ? customTotal : calculatedScrapCartTotal;

  // Satış personeli seçimi
  const selectStaff = (staff) => {
    setFormData({
      ...formData,
      staff_id: staff.id,
      staff_name: staff.name
    });
  };

  // Döviz kuru hesaplama - makas farkı ile (RetailSales ile aynı mantık)
  const getPaymentExchangeRate = (currency) => {
    const customRate = customRates[currency];
    let liveAlis;

    if (currency === 'USD') {
      liveAlis = parseFloat(prices?.USD_alis) || parseFloat(prices?.USD) || 1;
    } else if (currency === 'EUR') {
      liveAlis = parseFloat(prices?.EUR_alis) || parseFloat(prices?.EUR) || 1;
    } else if (currency === 'GOLD') {
      liveAlis = parseFloat(prices?.altin_alis) || 1;
    } else if (currency === 'SILVER') {
      liveAlis = parseFloat(prices?.gumus_alis) || 1;
    } else {
      return 1;
    }

    // Önce makas farkı yüzdesini kontrol et (dinamik hesaplama)
    if (customRate?.alis_percentage != null && customRate.alis_percentage !== '' && !isNaN(customRate.alis_percentage)) {
      return liveAlis * (1 - parseFloat(customRate.alis_percentage) / 100);
    }
    // Sonra manuel sabit alış kurunu kontrol et
    if (customRate?.rate_alis != null && parseFloat(customRate.rate_alis) > 0) {
      return parseFloat(customRate.rate_alis);
    }
    // Manuel alış kuru yoksa anlık alış kurunu kullan
    return liveAlis;
  };

  // Ödeme ekleme
  const addScrapPayment = (method, creditType = null, bankAccount = null) => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.warning('Lütfen geçerli bir tutar girin!');
      return;
    }

    const amount = parseFloat(paymentAmount);
    const rate = getPaymentExchangeRate(selectedPaymentCurrency);
    const amountTL = selectedPaymentCurrency === 'TRY' ? amount : amount * rate;
    const bank = bankAccount || selectedBankAccount;

    const payment = {
      id: Date.now(),
      method: method,
      currency: selectedPaymentCurrency,
      amount: amount,
      amountTL: amountTL,
      rate: rate,
      creditType: creditType,
      bankAccountId: method === 'Havale' ? bank?.id : null,
      bankAccountName: method === 'Havale' ? bank?.bank_name : null,
      bankAccountType: method === 'Havale' ? bank?.account_type : null
    };

    setScrapPayments([...scrapPayments, payment]);
    setPaymentAmount('');
    setSelectedPaymentCurrency('TRY');
    setShowPaymentCreditOptions(false);
    setSelectedBankAccount(null);
  };

  // Ödeme silme
  const removeScrapPayment = (paymentId) => {
    setScrapPayments(scrapPayments.filter(p => p.id !== paymentId));
  };

  // Ödeme toplamı hesaplama
  const getTotalPaidAmount = () => {
    return scrapPayments.reduce((sum, p) => sum + p.amountTL, 0);
  };

  // Kalan tutar hesaplama
  const getRemainingAmount = () => {
    const total = pendingScrapData?.totalAmount || 0;
    const paid = getTotalPaidAmount();
    const remaining = total - paid;
    return Math.abs(remaining) < 0.01 ? 0 : remaining;
  };

  // Havale seçimi için banka modalını aç
  const openBankSelection = () => {
    if (!paymentAmount || parseFloat(paymentAmount) <= 0) {
      toast.warning('Önce tutar girin!');
      return;
    }
    setPendingPaymentMethod('Havale');
    setShowBankModal(true);
  };

  // Banka seçildiğinde
  const handleBankSelect = (bank) => {
    setSelectedBankAccount(bank);
    setShowBankModal(false);
    addScrapPayment('Havale', null, bank);
  };

  const handleSubmit = async () => {
    if (formData.customer_phone && !isPhoneComplete(formData.customer_phone)) {
      toast.warning('Telefon numarası 11 haneli olmalı (0 dahil)');
      return;
    }
    // Düzenleme modunda validasyon
    if (editingPurchase) {
      if (!formData.customer_name || !formData.gross_weight) {
        toast.warning('Müşteri adı ve brüt gram gerekli!');
        return;
      }
      if (!formData.unit_price || parseFloat(formData.unit_price) <= 0) {
        toast.warning('Birim fiyat (Has Gr Fiyatı) zorunludur!');
        return;
      }
    } else {
      // Yeni alım modunda: müşteri bilgisi ve sepet veya manuel giriş gerekli
      if (!formData.customer_name) {
        toast.warning('Müşteri adı gerekli!');
        return;
      }
      if (scrapCart.length === 0 && !formData.gross_weight) {
        toast.warning('Sepete hurda ekleyin veya manuel olarak brüt gram girin!');
        return;
      }
      // Sepet boşsa ve manuel giriş yapılıyorsa birim fiyat zorunlu
      if (scrapCart.length === 0 && formData.gross_weight && (!formData.unit_price || parseFloat(formData.unit_price) <= 0)) {
        toast.warning('Birim fiyat (Has Gr Fiyatı) zorunludur!');
        return;
      }
    }

    try {
      let customerId = formData.customer_id;

      // Yeni müşteri modunda ve müşteri seçilmemişse yeni müşteri oluştur
      if (customerMode === 'new' && !customerId && formData.customer_name) {
        const customerResponse = await fetch(`${API_URL}/customers`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({
            name: formData.customer_name,
            phone: formData.customer_phone || '',
            store_type: isGoldStore ? 'Altın' : 'Gümüş'
          })
        });
        const customerData = await customerResponse.json();
        if (customerData.success && customerData.customer) {
          customerId = customerData.customer.id;
          toast.info('Yeni müşteri kaydedildi!');
          fetchCustomers(); // Müşteri listesini güncelle
        } else if (customerData.duplicate && customerData.existing_customer) {
          // Aynı telefon+mağaza tipinde zaten cari var - mükerrer açmak yerine onu kullan
          customerId = customerData.existing_customer.id;
          toast.info(`Mevcut müşteri kullanıldı: ${customerData.existing_customer.name}`);
        } else if (!customerData.success) {
          toast.error(customerData.message || 'Müşteri kaydedilemedi!');
          return;
        }
      }

      // Düzenleme modunda eski davranış
      if (editingPurchase) {
        // Ödeme bilgilerini hesapla (eski ve yeni key format uyumluluğu)
        const paidCash = scrapPayments
          .filter(p => p.method === 'Nakit')
          .reduce((sum, p) => sum + (p.amountTL || p.amount_tl || 0), 0);
        const paidTransfer = scrapPayments
          .filter(p => p.method === 'Havale')
          .reduce((sum, p) => sum + (p.amountTL || p.amount_tl || 0), 0);
        const paidCredit = scrapPayments
          .filter(p => p.method?.includes('Açık Hesap'))
          .reduce((sum, p) => sum + (p.amountTL || p.amount_tl || 0), 0);

        // Ödeme yöntemini belirle
        let paymentMethod = '';
        const methodTypes = [];
        if (paidCash > 0) methodTypes.push('Nakit');
        if (paidTransfer > 0) methodTypes.push('Havale');
        if (paidCredit > 0) methodTypes.push('Açık Hesap');
        paymentMethod = methodTypes.length > 1 ? 'Karışık' : (methodTypes[0] || '');

        const purchaseData = {
          ...formData,
          customer_id: customerId,
          pure_weight: calculatePureWeight(),
          total_amount: calculateTotal(),
          payment_method: paymentMethod,
          payment_details: JSON.stringify(scrapPayments),
          paid_cash: paidCash,
          paid_transfer: paidTransfer,
          paid_credit: paidCredit,
          bank_account_id: scrapPayments.find(p => p.bankAccountId)?.bankAccountId || null
        };

        const response = await fetch(`${API_URL}/scrap-purchases/${editingPurchase.id}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(purchaseData)
        });

        const data = await response.json();
        if (data.success) {
          const metalTuru = formData.metal_type === 'gold' ? 'Altın' : 'Gümüş';
          const pureWeight = calculatePureWeight();
          const totalAmount = calculateTotal();

          // Değişiklikleri tespit et
          const changes = [];
          const eskiMetalTuru = originalPurchase?.metal_type === 'gold' ? 'Altın' : 'Gümüş';

          if (originalPurchase?.customer_name !== formData.customer_name) {
            changes.push(`Müşteri: "${originalPurchase?.customer_name}" → "${formData.customer_name}"`);
          }
          if (originalPurchase?.customer_phone !== formData.customer_phone) {
            changes.push(`Telefon: "${originalPurchase?.customer_phone || '-'}" → "${formData.customer_phone || '-'}"`);
          }
          if (originalPurchase?.metal_type !== formData.metal_type) {
            changes.push(`Metal Türü: ${eskiMetalTuru} → ${metalTuru}`);
          }
          if (originalPurchase?.purity !== formData.purity) {
            changes.push(`Ayar: ${originalPurchase?.purity} Ayar → ${formData.purity} Ayar`);
          }
          if (originalPurchase?.gross_weight !== formData.gross_weight) {
            changes.push(`Brüt Gram: ${originalPurchase?.gross_weight} gr → ${formData.gross_weight} gr`);
          }
          if (originalPurchase?.waste_gram !== formData.waste_gram) {
            changes.push(`Fire: ${originalPurchase?.waste_gram || 0} gr → ${formData.waste_gram || 0} gr`);
          }
          if (originalPurchase?.pure_weight !== pureWeight) {
            changes.push(`Has Gram: ${originalPurchase?.pure_weight} gr → ${pureWeight} gr`);
          }
          if (originalPurchase?.unit_price !== formData.unit_price) {
            changes.push(`Birim Fiyat: ₺${Number(originalPurchase?.unit_price || 0).toLocaleString()} → ₺${Number(formData.unit_price).toLocaleString()}`);
          }
          if (parseFloat(originalPurchase?.total_amount || 0).toFixed(2) !== parseFloat(totalAmount).toFixed(2)) {
            changes.push(`Toplam Tutar: ₺${Number(originalPurchase?.total_amount || 0).toLocaleString()} → ₺${Number(totalAmount).toLocaleString()}`);
          }
          if (originalPurchase?.staff_name !== formData.staff_name) {
            changes.push(`Personel: "${originalPurchase?.staff_name || '-'}" → "${formData.staff_name || '-'}"`);
          }
          if (originalPurchase?.notes !== formData.notes) {
            if (originalPurchase?.notes && formData.notes) {
              changes.push(`Notlar güncellendi`);
            } else if (formData.notes) {
              changes.push(`Not eklendi: "${formData.notes}"`);
            } else {
              changes.push(`Not silindi`);
            }
          }

          logService.log('scrap', 'update',
            `Hurda alımı güncellendi: ${formData.customer_name} - ${changes.length} değişiklik`, {
            entity_type: 'scrap_purchase',
            entity_id: editingPurchase.id,
            details: {
              musteri: formData.customer_name,
              degisiklik_sayisi: changes.length,
              degisiklikler: changes.length > 0 ? changes : ['Değişiklik yapılmadı'],
              onceki_degerler: {
                musteri: originalPurchase?.customer_name,
                metal_turu: eskiMetalTuru,
                ayar: `${originalPurchase?.purity} Ayar`,
                brut_gram: `${originalPurchase?.gross_weight} gr`,
                has_gram: `${originalPurchase?.pure_weight} gr`,
                toplam_tutar: `₺${Number(originalPurchase?.total_amount || 0).toLocaleString()}`
              },
              yeni_degerler: {
                musteri: formData.customer_name,
                metal_turu: metalTuru,
                ayar: `${formData.purity} Ayar`,
                brut_gram: `${formData.gross_weight} gr`,
                has_gram: `${pureWeight} gr`,
                toplam_tutar: `₺${Number(totalAmount).toLocaleString()}`
              }
            },
            status: 'success'
          });

          toast.success('Hurda alımı güncellendi!');
          setShowModal(false);
          resetForm();
          fetchPurchases();
        } else {
          toast.error(data.error || 'Güncelleme başarısız!');
        }
        return;
      }

      // Yeni alım modunda - ödeme modalını aç
      const itemsToSave = scrapCart.length > 0 ? scrapCart : [{
        metal_type: formData.metal_type,
        purity: formData.purity,
        gross_weight: parseFloat(formData.gross_weight),
        waste_gram: parseFloat(formData.waste_gram) || 0,
        pure_weight: parseFloat(calculatePureWeight()),
        unit_price: parseFloat(formData.unit_price),
        total: parseFloat(calculateTotal()),
        quantity: 1,
        name: formData.notes || `${formData.metal_type === 'gold' ? 'Altın' : 'Gümüş'} ${formData.purity} Ayar`
      }];

      // Toplam tutarı hesapla (manuel toplam varsa onu kullan)
      let totalAmount = 0;
      if (customTotal !== null) {
        totalAmount = customTotal;
      } else {
        for (const item of itemsToSave) {
          totalAmount += (item.unit_total || item.total) * item.quantity;
        }
      }

      // Ödeme modalı için verileri hazırla
      setPendingScrapData({
        customerId,
        itemsToSave,
        totalAmount: totalAmount,
        formData: { ...formData }
      });

      // Ödeme state'lerini sıfırla
      setScrapPayments([]);
      setPaymentAmount('');
      setSelectedPaymentCurrency('TRY');
      setShowPaymentCreditOptions(false);

      // Ödeme modalını aç
      setShowPaymentModal(true);

    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error('Sunucu hatası! Lütfen tekrar deneyin.');
    }
  };

  // Ödeme modalı onaylandığında çağrılacak
  const completeScrapPurchase = async () => {
    if (!pendingScrapData) return;

    const { customerId, itemsToSave, totalAmount, formData: savedFormData } = pendingScrapData;

    // Manuel toplam varsa, her item'ın tutarını oransal olarak dağıt
    const calculatedTotal = itemsToSave.reduce((sum, item) => sum + ((item.unit_total || item.total) * item.quantity), 0);
    const totalRatio = calculatedTotal > 0 ? totalAmount / calculatedTotal : 1;

    // Ödeme bilgilerini hazırla
    const paymentMethod = scrapPayments.length === 0 ? 'Belirtilmedi' :
      scrapPayments.length === 1 ? scrapPayments[0].method : 'Karışık';

    const paidCash = scrapPayments
      .filter(p => p.method === 'Nakit')
      .reduce((sum, p) => sum + (p.amountTL || p.amount_tl || 0), 0);

    const paidTransfer = scrapPayments
      .filter(p => p.method === 'Havale')
      .reduce((sum, p) => sum + (p.amountTL || p.amount_tl || 0), 0);

    const paidCredit = scrapPayments
      .filter(p => p.method?.includes('Açık Hesap'))
      .reduce((sum, p) => sum + (p.amountTL || p.amount_tl || 0), 0);

    const bankAccountId = scrapPayments.find(p => p.method === 'Havale')?.bankAccountId || null;

    let savedCount = 0;
    let totalSavedAmount = 0;

    try {
      // Mağazaya özel fiş numarasını al
      let storeScrapNumber = null;
      try {
        const numResponse = await fetch(`${API_URL}/scrap-purchases/next-number/${currentStoreId}`);
        const numData = await numResponse.json();
        storeScrapNumber = numData.next_number;
      } catch (e) {
        console.error('Fiş numarası alınamadı:', e);
      }

      // Grup ID oluştur (timestamp + random)
      const groupId = `GRP_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

      for (const item of itemsToSave) {
        for (let i = 0; i < item.quantity; i++) {
          const purchaseData = {
            customer_id: customerId,
            customer_name: savedFormData.customer_name,
            customer_phone: savedFormData.customer_phone,
            staff_id: savedFormData.staff_id,
            staff_name: savedFormData.staff_name,
            store_id: currentStoreId,
            metal_type: item.metal_type,
            purity: item.purity,
            gross_weight: item.gross_weight,
            waste_gram: item.waste_gram || 0,
            pure_weight: item.pure_weight,
            unit_price: item.unit_price,
            total_amount: (item.unit_total || item.total) * totalRatio,
            // Sistemin ilk hesapladığı tutar (manuel düzenleme öncesi) — sadece bilgi amaçlı,
            // fiş/rapor hiçbir hesaplamada kullanılmaz, yalnızca detay ekranında referans olarak gösterilir.
            calculated_amount: item.unit_total || item.total,
            bonus_percent: item.bonus_percent || 0,
            notes: item.name || '',
            payment_method: paymentMethod,
            payment_details: scrapPayments.map(p => ({
              method: p.method,
              currency: p.currency,
              amount: p.amount,
              amountTL: p.amountTL,
              rate: p.rate,
              creditType: p.creditType,
              bankAccountId: p.bankAccountId,
              bankAccountName: p.bankAccountName,
              bankAccountType: p.bankAccountType
            })),
            paid_cash: paidCash / itemsToSave.reduce((sum, it) => sum + it.quantity, 0),
            paid_transfer: paidTransfer / itemsToSave.reduce((sum, it) => sum + it.quantity, 0),
            paid_credit: paidCredit / itemsToSave.reduce((sum, it) => sum + it.quantity, 0),
            bank_account_id: bankAccountId,
            group_id: groupId,
            store_scrap_number: storeScrapNumber
          };

          const response = await fetch(`${API_URL}/scrap-purchases`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify(purchaseData)
          });

          const data = await response.json();
          if (data.success) {
            savedCount++;
            totalSavedAmount += parseFloat(item.unit_total || item.total) * totalRatio;
          }
        }
      }

      if (savedCount > 0) {
        const metalTuru = isGoldStore ? 'Altın' : 'Gümüş';

        // Açık hesap ödemeleri varsa müşteri alacağını doğrudan bakiye sistemine
        // (customer_balance_transactions — bakiyenin TEK kaynağı) yaz. Eskiden burada ayrı
        // bir gift_cards kaydı/kodu oluşturuluyordu; bu, müşterinin ana bakiye ekranında/
        // geçmişinde hiç görünmüyordu (sadece ayrı "Alacak Raporu" üzerinden görülebiliyordu)
        // ve personel kodu unutup krediyi kaybedebiliyordu. Negatif amount/amount_tl = alacak.
        const creditPayments = scrapPayments.filter(p => p.method?.includes('Açık Hesap'));
        let balanceCreditsCreated = 0;

        for (const creditPayment of creditPayments) {
          try {
            // Borç tipini belirle
            let debtType = 'TL';
            if (creditPayment.method === 'Açık Hesap (Gram)') debtType = 'GRAM';
            else if (creditPayment.method === 'Açık Hesap (Gümüş)') debtType = 'GUMUS';

            const isGramBased = debtType === 'GRAM' || debtType === 'GUMUS';
            const gramAmount = isGramBased ? parseFloat(creditPayment.amount) : null;
            const rateAtCreation = isGramBased ? parseFloat(creditPayment.rate || creditPayment.goldRate || creditPayment.silverRate) : null;
            const tlAmount = isGramBased ? gramAmount * rateAtCreation : (parseFloat(creditPayment.amountTL || creditPayment.amount_tl) || 0);

            const debtPayload = {
              debt_type: debtType,
              amount: -(isGramBased ? gramAmount : tlAmount),
              amount_tl: -tlAmount,
              gold_rate: debtType === 'GRAM' ? rateAtCreation : null,
              silver_rate: debtType === 'GUMUS' ? rateAtCreation : null,
              description: `Hurda Alımı - ${savedFormData.customer_name} - ${debtType === 'GRAM' ? 'Altın' : debtType === 'GUMUS' ? 'Gümüş' : 'TL'} Borcu${gramAmount ? ` (${gramAmount.toFixed(2)} gr)` : ''}`
            };

            const debtResponse = await fetch(`${API_URL}/customers/${customerId}/debts`, {
              method: 'POST',
              headers: { 'Content-Type': 'application/json' },
              body: JSON.stringify(debtPayload)
            });

            const debtData = await debtResponse.json();
            if (debtData.success) {
              balanceCreditsCreated++;
            }
          } catch (balanceError) {
            console.error('Bakiye oluşturma hatası:', balanceError);
          }
        }

        // Bakiyeler oluşturulduysa bilgi ver
        if (balanceCreditsCreated > 0) {
          toast.info(`Müşteri bakiyesine ${balanceCreditsCreated} adet hurda alacağı eklendi`, { autoClose: 8000 });
        }

        logService.log('scrap', 'create',
          `Toplu hurda alımı: ${savedFormData.customer_name} - ${savedCount} adet - ₺${totalSavedAmount.toLocaleString('tr-TR')}`, {
          entity_type: 'scrap_purchase',
          details: {
            musteri: savedFormData.customer_name,
            telefon: savedFormData.customer_phone || '-',
            adet: savedCount,
            toplam_tutar: `₺${totalSavedAmount.toLocaleString('tr-TR')}`,
            odeme_yontemi: paymentMethod,
            nakit: `₺${paidCash.toLocaleString('tr-TR')}`,
            havale: `₺${paidTransfer.toLocaleString('tr-TR')}`,
            acik_hesap: `₺${paidCredit.toLocaleString('tr-TR')}`,
            personel: savedFormData.staff_name || '-',
            yeni_musteri: customerMode === 'new' ? 'Evet' : 'Hayır',
            urunler: itemsToSave.map(item => `${item.name} x${item.quantity}`),
            bakiye_alacagi_sayisi: balanceCreditsCreated
          },
          status: 'success'
        });

        toast.success(`${savedCount} adet hurda alımı kaydedildi! Toplam: ₺${totalSavedAmount.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`);

        // Fiş yazdırma için verileri kaydet
        const scrapReceiptData = {
          customerName: savedFormData.customer_name,
          customerPhone: savedFormData.customer_phone || '',
          staffName: savedFormData.staff_name || '',
          items: itemsToSave.map(item => {
            const itemPurityRate = item.purity_rate || purityRates[item.metal_type]?.[item.purity] || defaultPurityRates[item.metal_type]?.[item.purity] || 0;
            return {
              name: item.name || `${item.metal_type === 'gold' ? 'Altın' : 'Gümüş'} ${item.purity} Ayar`,
              metalType: item.metal_type,
              purity: item.purity,
              milyem: Math.round(itemPurityRate * 1000),
              grossWeight: item.gross_weight,
              wasteGram: item.waste_gram || 0,
              pureWeight: item.pure_weight,
              unitPrice: item.unit_price,
              total: (item.unit_total || item.total) * totalRatio,
              quantity: item.quantity || 1
            };
          }),
          totalAmount: totalSavedAmount,
          paymentMethod: paymentMethod,
          payments: scrapPayments.map(p => ({
            method: p.method,
            currency: p.currency,
            amount: p.amount,
            amountTL: p.amountTL || p.amount_tl,
            rate: p.rate,
            bankAccountName: p.bankAccountName || null,
            bankAccountType: p.bankAccountType || null
          })),
          storeScrapNumber: storeScrapNumber,
          date: new Date(),
          storeId: currentStoreId
        };

        setShowPaymentModal(false);
        setShowModal(false);
        setPendingScrapData(null);
        resetForm();
        clearScrapCart();
        fetchPurchases();

        // Bilgi fişi yazdırmak ister misiniz? (iOS WebView'da tarayıcı yazdırması çalışmadığı için sorulmaz)
        if (!mobileApp) showConfirmation({
          title: 'Fiş Yazdır',
          message: 'Hurda alım bilgi fişi yazdırmak ister misiniz?',
          confirmText: 'Evet, Yazdır',
          cancelText: 'Hayır',
          type: 'info',
          onConfirm: () => printScrapReceipt(scrapReceiptData)
        });
      } else {
        toast.error('Kayıt başarısız!');
        logService.log('scrap', 'create',
          `Hurda alımı kaydedilemedi: ${savedFormData.customer_name}`, {
          entity_type: 'scrap_purchase',
          details: { musteri: savedFormData.customer_name },
          status: 'error'
        });
      }
    } catch (error) {
      console.error('Kayıt hatası:', error);
      toast.error('Sunucu hatası! Lütfen tekrar deneyin.');
      logService.log('scrap', 'create',
        `Hurda alımı hatası: ${savedFormData.customer_name}`, {
        entity_type: 'scrap_purchase',
        details: { musteri: savedFormData.customer_name, hata: error.message },
        status: 'error'
      });
    }
  };

  // Hurda alım bilgi fişi yazdır (80mm termal yazıcı formatı)
  const printScrapReceipt = (data) => {
    const formatDate = (date) => {
      return new Date(date).toLocaleString('tr-TR', {
        day: '2-digit', month: '2-digit', year: 'numeric',
        hour: '2-digit', minute: '2-digit'
      });
    };
    const formatMoney = (amount) => {
      return Number(amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
    };
    const maskName = (name) => {
      if (!name) return '';
      return name.split(' ').map(word => {
        if (word.length <= 1) return word;
        return word[0] + '*'.repeat(word.length - 1);
      }).join(' ');
    };

    const itemsHtml = data.items.map(item => {
      const netWeight = Number(item.grossWeight) - Number(item.wasteGram || 0);
      return `
        <div class="item">
          <div class="item-name">${item.name}${item.milyem ? ` (${item.milyem} Milyem)` : ''}</div>
          <div class="item-detail">Brüt: ${Number(item.grossWeight).toFixed(2)} gr${item.wasteGram > 0 ? ` | Fire: ${Number(item.wasteGram).toFixed(2)} gr` : ''}</div>
          <div class="item-detail">${item.wasteGram > 0 ? `Net: ${netWeight.toFixed(2)} gr | ` : ''}Has: ${Number(item.pureWeight).toFixed(2)} gr${item.quantity > 1 ? ` × ${item.quantity} adet` : ''}</div>
          <div class="item-price-row">
            <span>₺${formatMoney(item.unitPrice)}/gr</span>
            <span class="item-total">₺${formatMoney(item.total)}</span>
          </div>
        </div>
      `;
    }).join('');

    let paymentHtml = '';
    if (data.payments && data.payments.length > 0) {
      paymentHtml = data.payments.map(p => {
        const methodLabel = p.method === 'Havale' && p.bankAccountName ? `Havale (${p.bankAccountName})` : p.method;
        let line = `<div class="row"><span>${methodLabel}</span>`;
        if (p.currency && p.currency !== 'TRY') {
          line += `<span>${Number(p.amount).toLocaleString('tr-TR', {minimumFractionDigits: 2})} ${p.currency} (₺${formatMoney(p.amountTL)})</span>`;
        } else {
          line += `<span>₺${formatMoney(p.amountTL || p.amount)}</span>`;
        }
        line += `</div>`;
        return line;
      }).join('');
    } else {
      paymentHtml = `<div class="row"><span>${data.paymentMethod}</span><span>₺${formatMoney(data.totalAmount)}</span></div>`;
    }

    const receiptNo = data.storeScrapNumber || `HA-${Date.now().toString().slice(-8)}`;

    const scrapHtml = `
      <!DOCTYPE html>
      <html>
      <head>
        <title>Hurda Alım Fişi - ${receiptNo}</title>
        <script src="https://cdn.jsdelivr.net/npm/jsbarcode@3.11.6/dist/JsBarcode.all.min.js"><\/script>
        <style>
          @page { size: 80mm auto; margin: 5mm; }
          * { margin: 0; padding: 0; box-sizing: border-box; }
          body {
            font-family: Arial, Helvetica, sans-serif;
            font-size: 12px;
            font-weight: 600;
            width: 80mm;
            padding: 5mm;
            background: #fff;
            -webkit-print-color-adjust: exact;
            print-color-adjust: exact;
          }
          .header { text-align: center; padding-bottom: 4px; margin-bottom: 4px; }
          .logo-img { width: 120px; height: auto; margin-bottom: 8px; filter: invert(1); }
          .subtitle { font-size: 13px; font-weight: bold; margin-top: 6px; margin-bottom: 4px; }
          .date-line { text-align: center; font-size: 13px; color: #000; font-weight: 600; margin-bottom: 5px; }
          .divider { border-top: 1px dashed #333; margin: 8px 0; }
          .item { margin-bottom: 10px; padding-bottom: 6px; border-bottom: 1px dotted #ddd; }
          .item:last-child { border-bottom: none; }
          .item-name { font-weight: bold; font-size: 12px; margin-bottom: 2px; }
          .item-detail { font-size: 10px; color: #000; }
          .item-price-row { display: flex; justify-content: space-between; font-size: 11px; margin-top: 2px; }
          .item-total { font-weight: bold; }
          .row { display: flex; justify-content: space-between; font-size: 11px; padding: 3px 0; }
          .row-grand-total {
            display: flex; justify-content: space-between;
            font-size: 16px; font-weight: bold; padding: 8px 0;
            border-top: 2px solid #000; border-bottom: 2px solid #000; margin: 5px 0;
          }
          .section-label { font-size: 10px; font-weight: bold; color: #000; margin-bottom: 4px; }
          .customer-section { font-size: 11px; margin: 5px 0; }
          .staff-section { font-family: Arial, sans-serif; font-size: 10px; color: #000; margin: 5px 0; }
          .barcode-section { text-align: center; margin: 10px 0; }
          .barcode-number { font-size: 10px; color: #000; margin-top: 2px; }
          .footer { text-align: center; margin-top: 10px; padding-top: 10px; font-size: 10px; color: #000; }
          @media print { body { width: 80mm; } .no-print { display: none; } }
        </style>
      </head>
      <body>
        <div class="header">
          ${data.storeId === 1
            ? '<img src="/Siyah - Beyaz - 1.png" alt="Akik Gold Jewellery" class="logo-img" />'
            : '<div style="margin: 0 auto 2px auto; text-align: center;"><img src="/akikfisligi.png" alt="Akik Gümüş" style="width: 170px; height: auto; filter: contrast(3);" /></div>'
          }
          ${data.storeId === 1
            ? '<div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 2px;">Osmangazi Mah. Ahmet Şireci Bulvarı No: 14D<br/>Şehitkamil / Gaziantep<br/>Tel: 0342 230 00 64<br/>www.akikjewellery.com | info@akikjewellery.com</div>'
            : `<div style="font-family: Arial, sans-serif; font-size: 11px; font-weight: bold; color: #000; margin-bottom: 3px;">${data.storeId === 3 ? '(YEŞİLSU ŞUBESİ)' : '(OSMANGAZİ ŞUBESİ)'}</div>
               <div style="font-family: Arial, sans-serif; font-size: 10px; color: #000; line-height: 1.4; margin-bottom: 2px;">${data.storeId === 3 ? 'Bey Mah. Atatürk Blv. 43/A<br/>Şahinbey / Gaziantep' : 'Osmangazi Mah. Ahmet Şireci Bulvarı No: 16/F<br/>Şehitkamil / Gaziantep'}<br/>Tel: 0342 230 00 64</div>`
          }
          <div class="subtitle">HURDA ALIM FİŞİ</div>
        </div>

        <div class="date-line">${formatDate(data.date)}</div>

        <div class="divider"></div>

        <div class="customer-section">
          <span style="color: #000; font-weight: bold;">Müşteri:</span> ${data.customerName}
          ${data.customerPhone ? `<br/><span style="color: #000; font-weight: bold;">Tel:</span> ${data.customerPhone}` : ''}
        </div>

        <div class="divider"></div>

        ${itemsHtml}

        <div class="divider"></div>

        <div class="row-grand-total">
          <span>TOPLAM:</span>
          <span>₺${formatMoney(data.totalAmount)}</span>
        </div>

        <div style="margin: 8px 0;">
          <div class="section-label">ÖDEME:</div>
          ${paymentHtml}
        </div>

        ${data.staffName ? `<div class="staff-section">Personel: ${maskName(data.staffName)}</div>` : ''}

        <div class="divider"></div>

        <div class="barcode-section">
          <svg id="barcode-scrap"></svg>
          <div class="barcode-number">${receiptNo}</div>
        </div>

        <div class="footer">
          <div style="font-weight: bold;">TEŞEKKÜR EDERİZ YİNE BEKLERİZ</div>
          <div style="font-weight: bold;">BİLGİ FİŞİDİR MALİ DEĞERİ YOKTUR</div>
        </div>

        <script>
          window.onload = function() {
            try {
              JsBarcode("#barcode-scrap", "${receiptNo}", {
                format: "CODE128", width: 1.5, height: 40,
                displayValue: false, margin: 0, background: "transparent"
              });
            } catch(e) { console.error('Barkod hatası:', e); }
            var directPrint = localStorage.getItem('directPrintEnabled') === 'true';
            setTimeout(function() {
              window.print();
              if (directPrint) { setTimeout(function() { window.close(); }, 500); }
            }, 300);
          }
        </script>
      </body>
      </html>
    `;
    handlePrint(scrapHtml, 'receipt');
  };

  // Geçmiş hurda alımından fiş yazdır
  const reprintScrapReceipt = async (purchase) => {
    try {
      let items = [];
      let totalAmount = Number(purchase.total_amount) || 0;
      let rawPaymentDetails = null;

      // Gruplu alım ise detayları çek
      if (purchase.group_id && purchase.item_count > 1) {
        const response = await fetch(`${API_URL}/scrap-purchases/by-group/${purchase.group_id}`);
        const data = await response.json();
        const groupItems = Array.isArray(data) ? data : [];
        items = groupItems.map(item => {
          const rate = purityRates[item.metal_type]?.[item.purity] || defaultPurityRates[item.metal_type]?.[item.purity] || 0;
          return {
            name: `${item.metal_type === 'gold' ? 'Altın' : 'Gümüş'} ${item.purity} Ayar`,
            metalType: item.metal_type,
            purity: item.purity,
            milyem: Math.round(rate * 1000),
            grossWeight: item.gross_weight,
            wasteGram: item.waste_gram || 0,
            pureWeight: item.pure_weight,
            unitPrice: item.unit_price,
            total: Number(item.total_amount) || 0,
            quantity: item.quantity || 1
          };
        });
        rawPaymentDetails = groupItems[0]?.payment_details;
        totalAmount = groupItems.reduce((sum, item) => sum + (Number(item.total_amount) || 0), 0);
      } else {
        // Tekli alım - detayı backend'den çek
        const purchaseId = purchase.first_id || purchase.id;
        const response = await fetch(`${API_URL}/scrap-purchases/${purchaseId}`);
        const fullPurchase = response.ok ? await response.json() : purchase;
        const metalType = fullPurchase.metal_type || purchase.metal_type || 'gold';
        const purity = fullPurchase.purity || purchase.purity || '22';
        const rate = purityRates[metalType]?.[purity] || defaultPurityRates[metalType]?.[purity] || 0;
        items = [{
          name: `${metalType === 'gold' ? 'Altın' : 'Gümüş'} ${purity} Ayar`,
          metalType: metalType,
          purity: purity,
          milyem: Math.round(rate * 1000),
          grossWeight: fullPurchase.gross_weight || purchase.gross_weight,
          wasteGram: fullPurchase.waste_gram || purchase.waste_gram || 0,
          pureWeight: fullPurchase.pure_weight || purchase.pure_weight,
          unitPrice: fullPurchase.unit_price || purchase.unit_price,
          total: Number(fullPurchase.total_amount || purchase.total_amount) || 0,
          quantity: fullPurchase.quantity || purchase.quantity || 1
        }];
        rawPaymentDetails = fullPurchase.payment_details;
        totalAmount = Number(fullPurchase.total_amount || purchase.total_amount) || 0;
      }

      const receiptData = {
        customerName: purchase.customer_name || '-',
        customerPhone: purchase.customer_phone || '',
        staffName: purchase.staff_name || '',
        items,
        totalAmount,
        paymentMethod: purchase.payment_method || 'Nakit',
        payments: [],
        storeScrapNumber: purchase.store_scrap_number || purchase.group_id || `HA-${purchase.id}`,
        date: purchase.created_at || new Date(),
        storeId: Number(purchase.store_id) || currentStoreId
      };

      // Ödeme detaylarını ekle - önce payment_details JSON'dan dene (bankAccountType bilgisi için)
      let savedPayments = [];
      try {
        if (rawPaymentDetails) {
          savedPayments = typeof rawPaymentDetails === 'string' ? JSON.parse(rawPaymentDetails) : rawPaymentDetails;
        }
      } catch(e) { savedPayments = []; }
      const havaleDetail = Array.isArray(savedPayments) ? savedPayments.find(p => p.method === 'Havale') : null;

      if (purchase.paid_cash > 0) receiptData.payments.push({ method: 'Nakit', currency: 'TRY', amount: purchase.paid_cash, amountTL: purchase.paid_cash });
      if (purchase.paid_transfer > 0) receiptData.payments.push({ method: 'Havale', currency: 'TRY', amount: purchase.paid_transfer, amountTL: purchase.paid_transfer, bankAccountName: havaleDetail?.bankAccountName || purchase.bank_account_name || purchase.bank_name || null, bankAccountType: havaleDetail?.bankAccountType || null });
      if (purchase.paid_credit > 0) receiptData.payments.push({ method: 'Kredi Kartı', currency: 'TRY', amount: purchase.paid_credit, amountTL: purchase.paid_credit });

      printScrapReceipt(receiptData);
    } catch (error) {
      console.error('Fiş yazdırma hatası:', error);
      toast.error('Fiş yazdırılamadı!');
    }
  };

  const handleDelete = async (purchase) => {
    // Gruplu kayıtlar için group_id ile sil, tekli kayıtlar için id ile sil
    const hasGroup = purchase.group_id && purchase.item_count > 0;
    const confirmMsg = hasGroup
      ? `Bu hurda alım grubunu (${purchase.item_count} kayıt) silmek istediğinize emin misiniz?`
      : 'Bu hurda alım kaydını silmek istediğinize emin misiniz?';

    showConfirmation({
      title: 'Hurda Alımı Sil',
      message: confirmMsg,
      confirmText: 'Evet, Sil',
      cancelText: 'İptal',
      type: 'danger',
      onConfirm: async () => {
        const metalTuru = purchase.metal_type === 'gold' ? 'Altın' : (purchase.metal_types === 'gold' ? 'Altın' : 'Gümüş');
        const purchaseId = purchase.id || purchase.first_id;
        const deleteUrl = hasGroup
          ? `${API_URL}/scrap-purchases/group/${purchase.group_id}`
          : `${API_URL}/scrap-purchases/${purchaseId}`;

        try {
          const response = await fetch(deleteUrl, {
            method: 'DELETE'
          });

          const data = await response.json();
          if (data.success) {
            logService.log('scrap', 'delete',
              `Hurda alımı silindi: ${purchase.customer_name} - ${metalTuru} ${purchase.purity || purchase.purities} Ayar`, {
              entity_type: 'scrap_purchase',
              entity_id: purchaseId,
              details: {
                musteri: purchase.customer_name,
                telefon: purchase.customer_phone || '-',
                metal_turu: metalTuru,
                ayar: `${purchase.purity} Ayar`,
                brut_gram: `${purchase.gross_weight} gr`,
                has_gram: `${purchase.pure_weight} gr`,
                toplam_tutar: `₺${Number(purchase.total_amount).toLocaleString()}`,
                personel: purchase.staff_name || '-'
              },
              status: 'success'
            });
            toast.success('Hurda alımı silindi!');
            fetchPurchases();
          } else {
            toast.error('Silme işlemi başarısız!');
            logService.log('scrap', 'delete',
              `Hurda alımı silinemedi: ${purchase.customer_name}`, {
              entity_type: 'scrap_purchase',
              entity_id: purchaseId,
              details: { musteri: purchase.customer_name, hata: data.error },
              status: 'error'
            });
          }
        } catch (error) {
          console.error('Silme hatası:', error);
          toast.error('Sunucu hatası!');
          logService.log('scrap', 'delete',
            `Hurda silme hatası: ${purchase.customer_name}`, {
            entity_type: 'scrap_purchase',
            entity_id: purchaseId,
            details: { musteri: purchase.customer_name, hata: error.message },
            status: 'error'
          });
        }
      }
    });
  };

  const openEditModal = async (purchase) => {
    try {
      // Gruplu özetle geliyorsa tam veriyi backend'den çek
      let fullPurchase = purchase;

      // Tam veriyi backend'den çek (gruplu özetten geldiğinde customer_id, staff_id gibi alanlar eksik olabilir)
      const purchaseId = purchase.first_id || purchase.id;
      const response = await fetch(`${API_URL}/scrap-purchases/${purchaseId}`);
      if (response.ok) {
        fullPurchase = await response.json();
      }

      setEditingPurchase(fullPurchase);
      // Orijinal değerleri kaydet (değişiklik tespiti için)
      setOriginalPurchase({
        customer_id: fullPurchase.customer_id || '',
        customer_name: fullPurchase.customer_name || '',
        customer_phone: fullPurchase.customer_phone || '',
        staff_id: fullPurchase.staff_id || '',
        staff_name: fullPurchase.staff_name || '',
        metal_type: fullPurchase.metal_type || 'gold',
        purity: fullPurchase.purity || '22',
        gross_weight: fullPurchase.gross_weight || '',
        waste_percent: fullPurchase.waste_percent || '',
        waste_gram: fullPurchase.waste_gram || '',
        unit_price: fullPurchase.unit_price || '',
        bonus_percent: fullPurchase.bonus_percent || '',
        notes: fullPurchase.notes || '',
        pure_weight: fullPurchase.pure_weight || '',
        total_amount: fullPurchase.total_amount || '',
        payment_method: fullPurchase.payment_method || '',
        paid_cash: fullPurchase.paid_cash || 0,
        paid_transfer: fullPurchase.paid_transfer || 0,
        paid_credit: fullPurchase.paid_credit || 0
      });
      // Fire yüzdesini hesapla (eğer kayıtlı değilse)
      const grossWeight = parseFloat(fullPurchase.gross_weight) || 0;
      const wasteGram = parseFloat(fullPurchase.waste_gram) || 0;
      const calculatedPercent = grossWeight > 0 ? (wasteGram / grossWeight * 100).toFixed(1) : '';
      // Tarih/saat formatla (datetime-local input için)
      let formattedDate = '';
      if (fullPurchase.created_at) {
        const date = new Date(fullPurchase.created_at);
        // Local time olarak formatla (datetime-local input için)
        const pad = (n) => n.toString().padStart(2, '0');
        formattedDate = `${date.getFullYear()}-${pad(date.getMonth()+1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`;
      }
      setFormData({
        customer_id: fullPurchase.customer_id || '',
        customer_name: fullPurchase.customer_name || '',
        customer_phone: fullPurchase.customer_phone || '',
        staff_id: fullPurchase.staff_id || '',
        staff_name: fullPurchase.staff_name || '',
        metal_type: fullPurchase.metal_type || 'gold',
        purity: fullPurchase.purity || '22',
        gross_weight: fullPurchase.gross_weight || '',
        waste_percent: fullPurchase.waste_percent || calculatedPercent,
        waste_gram: fullPurchase.waste_gram || '',
        unit_price: fullPurchase.unit_price || '',
        bonus_percent: fullPurchase.bonus_percent || '',
        notes: fullPurchase.notes || '',
        created_at: formattedDate
      });
      // Kayıtlı tutarı korumak için customTotal'ı set et (total_price gerçek tutarı tutar)
      const storedTotal = parseFloat(fullPurchase.total_price || fullPurchase.total_amount) || 0;
      if (storedTotal > 0) {
        setCustomTotal(storedTotal);
      }
      // Ödeme bilgilerini yükle
      let existingPayments = [];
      if (fullPurchase.payment_details) {
        try {
          existingPayments = typeof fullPurchase.payment_details === 'string'
            ? JSON.parse(fullPurchase.payment_details)
            : fullPurchase.payment_details;
        } catch (e) {
          existingPayments = [];
        }
      }
      setScrapPayments(existingPayments || []);
      setPaymentAmount('');
      setSelectedPaymentCurrency('TRY');
      setShowPaymentCreditOptions(false);
      // Müşteri seçim modunu ayarla
      if (fullPurchase.customer_id) {
        setCustomerMode('select');
      } else if (fullPurchase.customer_name && fullPurchase.customer_name !== 'Kayıtsız Müşteri') {
        setCustomerMode('new');
      } else {
        setCustomerMode('select');
      }
      setCustomerSearch('');
      setShowCustomerDropdown(false);
      setShowModal(true);
    } catch (error) {
      console.error('Hurda detayları yüklenemedi:', error);
      toast.error('Detaylar yüklenemedi!');
    }
  };

  const resetForm = () => {
    setEditingPurchase(null);
    setOriginalPurchase(null);
    setCustomerMode('select');
    setCustomerSearch('');
    setShowCustomerDropdown(false);
    setMatchingCustomer(null);
    // Ödeme bilgilerini temizle
    setScrapPayments([]);
    setPaymentAmount('');
    setSelectedPaymentCurrency('TRY');
    setShowPaymentCreditOptions(false);
    // Manuel toplam düzenlemeyi sıfırla
    setCustomTotal(null);
    setIsEditingTotal(false);
    setEditingTotalValue('');
    const currency = isGoldStore ? 'GOLD' : 'SILVER';
    setFormData({
      customer_id: '',
      customer_name: '',
      customer_phone: '',
      staff_id: '',
      staff_name: '',
      metal_type: storeMetalType,
      purity: isGoldStore ? '22' : '925',
      gross_weight: '',
      waste_percent: '',
      waste_gram: '',
      unit_price: getAlisPrice(currency).toString(),
      bonus_percent: '',
      notes: ''
    });
  };

  // Müşteri arama filtresi (mağaza türüne göre filtreleme)
  const filteredCustomers = customers.filter(c => {
    // Mağaza türüne göre müşteri filtrele
    const customerStoreType = (c.store_type || '').toLowerCase().trim();

    // store_type boş veya tanımsız ise tüm müşterileri göster (eski kayıtlar için uyumluluk)
    const hasStoreType = customerStoreType && customerStoreType !== '';

    if (hasStoreType) {
      // Altın mağazasında sadece altın müşterileri (gold veya altın)
      if (isGoldStore && customerStoreType !== 'gold' && customerStoreType !== 'altın') return false;
      // Gümüş mağazasında sadece gümüş müşterileri (silver veya gümüş)
      if (!isGoldStore && customerStoreType !== 'silver' && customerStoreType !== 'gümüş') return false;
    }

    // İsim veya telefon ile ara
    return c.name?.toLowerCase().includes(customerSearch.toLowerCase()) ||
           c.phone?.includes(customerSearch);
  }).slice(0, 10);

  const openNewModal = () => {
    resetForm();
    setShowModal(true);
  };

  const selectCustomer = (customer) => {
    setFormData({
      ...formData,
      customer_id: customer.id,
      customer_name: customer.name,
      customer_phone: customer.phone || ''
    });
    setMatchingCustomer(null); // Eşleşen müşteri uyarısını kaldır
  };

  // Telefon numarası ile müşteri kontrolü
  const checkPhoneNumber = (phone) => {
    if (!phone || phone.length < 5) {
      setMatchingCustomer(null);
      return;
    }
    // Telefon numarasını normalize et (sadece rakamlar)
    const normalizedPhone = phone.replace(/\D/g, '');
    // Mevcut müşteriler içinde ara
    const found = customers.find(c => {
      if (!c.phone) return false;
      const customerPhone = c.phone.replace(/\D/g, '');
      return customerPhone === normalizedPhone ||
             customerPhone.includes(normalizedPhone) ||
             normalizedPhone.includes(customerPhone);
    });
    setMatchingCustomer(found || null);
  };

  // Eşleşen müşteriyi seç
  const selectMatchingCustomer = () => {
    if (matchingCustomer) {
      selectCustomer(matchingCustomer);
      setCustomerMode('select');
      setMatchingCustomer(null);
      toast.success(`${matchingCustomer.name} müşterisi seçildi!`);
    }
  };

  const getMetalBadge = (type) => {
    return type === 'gold'
      ? <span className="metal-badge gold">Altın</span>
      : <span className="metal-badge silver">Gümüş</span>;
  };

  const filteredPurchases = purchases.filter(purchase => {
    const matchesSearch =
      purchase.customer_name?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      purchase.customer_phone?.includes(searchTerm);

    // Backend zaten store_id'ye göre filtreliyor
    return matchesSearch;
  });

  const stats = {
    total: filteredPurchases.length,
    totalAmount: filteredPurchases.reduce((sum, p) => sum + parseFloat(p.total_amount || 0), 0)
  };

  const renderMobileHome = () => (
    <ScrapMobileHome
      isAdmin={user?.role === 'admin'}
      storeId={currentStoreId}
      setStoreId={setMobileStoreId}
      loading={loading}
      purchases={filteredPurchases}
      stats={stats}
      searchTerm={searchTerm}
      setSearchTerm={setSearchTerm}
      getMilyem={getMilyemForPurchase}
      onOpen={openGroupDetail}
      onNew={openNewModal}
      onOpenLog={() => setShowLogModal(true)}
    />
  );

  return (
    <div className={mobileApp ? 'sp-mobile m-sheets' : 'scrap-purchase-container'}>
      {mobileApp ? renderMobileHome() : (<>
      {/* Header - RetailSales ile birebir aynı */}
      <header className="pos-header">
        <div className="header-left">
          <button className="back-btn" onClick={() => navigate(fromRetailSales ? '/retail-sales' : '/')}>
            <ArrowLeft size={18} /> {fromRetailSales ? 'Perakende Satışa Dön' : 'Ana Sayfa'}
          </button>
          <h1>Hurda Alımı</h1>
        </div>
        <div
          className="header-center"
          style={{
            // Görünen kur sayısına göre logo pozisyonunu ayarla
            left: (() => {
              const visibleCount = isGoldStore
                ? [visibleRates.gold, visibleRates.usd, visibleRates.eur, visibleRates.gbp, visibleRates.silverUsd].filter(Boolean).length
                : [visibleRates.silver, visibleRates.usd, visibleRates.eur, visibleRates.gbp, visibleRates.silverUsd].filter(Boolean).length;
              return visibleCount >= 5 ? 'calc(50% - 80px)' : '50%';
            })()
          }}
        >
          <img
            src={(user?.store_id === 1 || selectedStore?.id === 1) ? "/Siyah - Beyaz - 1.png" : "/logo.png"}
            alt="Akik"
            className="header-logo"
          />
        </div>
        <div className="header-right">
          <div className="live-prices">
            {/* Altın mağazası: Altın, USD, EUR göster / Gümüş mağazası: Gümüş, USD, EUR, GBP, Ag/USD göster */}
            {isGoldStore ? (
              <>
                {/* Altın Mağazası - Altın, USD, EUR sıralaması */}
                {visibleRates.gold && (
                  <div
                    className="price-item-group gold clickable"
                    onClick={() => openRateModal('GOLD')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">Altın</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">{prices?.altin_satis || '--'}</span>
                      <span className="rate buy">{prices?.altin_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.usd && (
                  <div
                    className="price-item-group usd clickable"
                    onClick={() => openRateModal('USD')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">$ USD</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">{prices?.USD_satis || '--'}</span>
                      <span className="rate buy">{prices?.USD_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.eur && (
                  <div
                    className="price-item-group eur clickable"
                    onClick={() => openRateModal('EUR')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">€ EUR</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">{prices?.EUR_satis || '--'}</span>
                      <span className="rate buy">{prices?.EUR_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.gbp && (
                  <div
                    className="price-item-group gbp"
                    title="Pound/TL Kuru"
                  >
                    <span className="price-item-label">£ GBP</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">{prices?.GBP_satis || '--'}</span>
                      <span className="rate buy">{prices?.GBP_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.silverUsd && (
                  <div
                    className="price-item-group silver-usd"
                    title="Gümüş/USD Paritesi"
                  >
                    <span className="price-item-label">Ag/USD</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">
                        {prices?.gumus_satis && prices?.USD_satis
                          ? (parseFloat(prices.gumus_satis) / parseFloat(prices.USD_satis)).toFixed(2)
                          : '--'}
                      </span>
                      <span className="rate buy">
                        {prices?.gumus_alis && prices?.USD_alis
                          ? (parseFloat(prices.gumus_alis) / parseFloat(prices.USD_alis)).toFixed(2)
                          : '--'}
                      </span>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <>
                {/* Gümüş Mağazaları - Gümüş, USD, EUR, GBP, Ag/USD sıralaması */}
                {visibleRates.silver && (
                  <div
                    className="price-item-group silver clickable"
                    onClick={() => openRateModal('SILVER')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">Gümüş</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">{prices?.gumus_satis || '--'}</span>
                      <span className="rate buy">{prices?.gumus_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.usd && (
                  <div
                    className="price-item-group usd clickable"
                    onClick={() => openRateModal('USD')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">$ USD</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">{prices?.USD_satis || '--'}</span>
                      <span className="rate buy">{prices?.USD_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.eur && (
                  <div
                    className="price-item-group eur clickable"
                    onClick={() => openRateModal('EUR')}
                    title={user?.role === 'admin' ? "Tıklayarak özel kur girebilirsiniz" : undefined}
                    style={user?.role !== 'admin' ? { cursor: 'default' } : undefined}
                  >
                    <span className="price-item-label">€ EUR</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">{prices?.EUR_satis || '--'}</span>
                      <span className="rate buy">{prices?.EUR_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.gbp && (
                  <div
                    className="price-item-group gbp"
                    title="Pound/TL Kuru"
                  >
                    <span className="price-item-label">£ GBP</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">{prices?.GBP_satis || '--'}</span>
                      <span className="rate buy">{prices?.GBP_alis || '--'}</span>
                    </div>
                  </div>
                )}
                {visibleRates.silverUsd && (
                  <div
                    className="price-item-group silver-usd"
                    title="Gümüş/USD Paritesi"
                  >
                    <span className="price-item-label">Ag/USD</span>
                    <div className="price-item-rates vertical">
                      <span className="rate sell">
                        {prices?.gumus_satis && prices?.USD_satis
                          ? (parseFloat(prices.gumus_satis) / parseFloat(prices.USD_satis)).toFixed(2)
                          : '--'}
                      </span>
                      <span className="rate buy">
                        {prices?.gumus_alis && prices?.USD_alis
                          ? (parseFloat(prices.gumus_alis) / parseFloat(prices.USD_alis)).toFixed(2)
                          : '--'}
                      </span>
                    </div>
                  </div>
                )}
              </>
            )}
            {/* Üç nokta menüsü */}
            <div className="rates-menu-container">
              <button
                className="rates-menu-btn"
                onClick={() => setShowRatesMenu(!showRatesMenu)}
                title="Pariteleri göster/gizle"
              >
                <MoreVertical size={16} />
              </button>
              {showRatesMenu && (
                <div className="rates-menu-dropdown">
                  <div className="rates-menu-title">Pariteleri Göster/Gizle</div>
                  {isGoldStore ? (
                    <>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.gold} onChange={() => toggleRateVisibility('gold')} />
                        <span>Altın</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.usd} onChange={() => toggleRateVisibility('usd')} />
                        <span>$ USD</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.eur} onChange={() => toggleRateVisibility('eur')} />
                        <span>€ EUR</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.gbp} onChange={() => toggleRateVisibility('gbp')} />
                        <span>£ GBP</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.silverUsd} onChange={() => toggleRateVisibility('silverUsd')} />
                        <span>Ag/USD</span>
                      </label>
                    </>
                  ) : (
                    <>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.silver} onChange={() => toggleRateVisibility('silver')} />
                        <span>Gümüş</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.usd} onChange={() => toggleRateVisibility('usd')} />
                        <span>$ USD</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.eur} onChange={() => toggleRateVisibility('eur')} />
                        <span>€ EUR</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.gbp} onChange={() => toggleRateVisibility('gbp')} />
                        <span>£ GBP</span>
                      </label>
                      <label className="rates-menu-item">
                        <input type="checkbox" checked={visibleRates.silverUsd} onChange={() => toggleRateVisibility('silverUsd')} />
                        <span>Ag/USD</span>
                      </label>
                    </>
                  )}
                </div>
              )}
            </div>
          </div>
          <div className="datetime">
            <span className="date">{currentTime.toLocaleDateString('tr-TR')}</span>
            <span className="time">{currentTime.toLocaleTimeString('tr-TR')}</span>
          </div>
        </div>
      </header>

      <div className="scrap-content">
        {/* Yardım Butonu */}
        <button className="help-btn-corner" title="Yardım" onClick={() => setShowHelpModal(true)}>?</button>

        {/* Yeni Alım Butonu */}
        <div className="content-header">
          <button className="action-header-btn log-btn" onClick={() => setShowLogModal(true)}>
            <ScrollText size={18} /> İşlem Geçmişi
          </button>
          <button className="action-header-btn" onClick={openNewModal}>
            <Plus size={18} /> Yeni Alım
          </button>
        </div>

        {/* Stats */}
        <div className="stats-row">
          <div className="stat-card">
            <div className="stat-icon-box">
              <BarChart3 size={24} style={{ color: '#6366f1' }} />
            </div>
            <div className="stat-info">
              <span className="stat-value">{stats.total}</span>
              <span className="stat-label">{isGoldStore ? 'Altın Alım' : 'Hurda Alım'}</span>
            </div>
          </div>
          <div className="stat-card success" style={{ flex: 1, minWidth: 0 }}>
            <div className="stat-icon-box success">
              <Coins size={24} style={{ color: '#10b981' }} />
            </div>
            <div className="stat-info" style={{ minWidth: 0 }}>
              <span className="stat-value" style={{
                fontSize: `₺${stats.totalAmount.toLocaleString()}`.length > 12 ? '1rem' : `₺${stats.totalAmount.toLocaleString()}`.length > 9 ? '1.1rem' : '1.25rem',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}>₺{stats.totalAmount.toLocaleString()}</span>
              <span className="stat-label">Toplam Tutar</span>
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="filters-row">
          <div className="search-box">
            <span className="search-icon">
              <Search size={18} />
            </span>
            <input
              type="text"
              placeholder="Müşteri adı veya telefon ara..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
            />
          </div>
          <div className="filter-buttons">
            <button className={`filter-btn active ${isGoldStore ? 'gold' : 'silver'}`} disabled>
              {isGoldStore ? 'Altın Hurdası' : 'Hurda Alımı'}
            </button>
          </div>
          <div className="column-settings-wrapper">
            <button
              className="column-settings-btn"
              onClick={() => setShowColumnSettings(!showColumnSettings)}
            >
              <Settings size={16} /> Sütunlar
            </button>
            {showColumnSettings && (
              <div className="column-settings-dropdown">
                <div className="column-settings-header">
                  <span>Görünür Sütunlar</span>
                  <button className="reset-columns-btn" onClick={resetColumns}>Sıfırla</button>
                </div>
                <div className="column-settings-list">
                  {allColumns.map(col => (
                    <label key={col.key} className="column-toggle">
                      <input
                        type="checkbox"
                        checked={visibleColumns[col.key]}
                        onChange={() => toggleColumn(col.key)}
                      />
                      <span>{col.label}</span>
                    </label>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>

        {/* Purchase List */}
        {loading ? (
          <div className="loading">Yükleniyor...</div>
        ) : filteredPurchases.length === 0 ? (
          <div className="empty-state">
            <Scale size={48} style={{ color: '#9ca3af' }} />
            <p>Hurda alım kaydı bulunamadı</p>
          </div>
        ) : (
          <div className="purchases-grid">
            <div className="purchases-header">
              {visibleColumns.type && <span>Tür</span>}
              {visibleColumns.customer && <span>Müşteri</span>}
              {visibleColumns.purity && <span>Ayar / Brüt</span>}
              {visibleColumns.pureWeight && <span>Has Gram</span>}
              {visibleColumns.unitPrice && <span>Birim Fiyat</span>}
              {visibleColumns.total && <span>Toplam</span>}
              {visibleColumns.paymentMethod && <span>Ödeme</span>}
              {visibleColumns.staff && <span>Personel</span>}
              {visibleColumns.date && <span>Tarih</span>}
              {visibleColumns.notes && <span>Notlar</span>}
              <span></span>
            </div>
            <div className="purchases-body">
            {filteredPurchases.map(purchase => (
              <div
                key={purchase.group_id || purchase.id}
                className="purchase-card clickable"
                onClick={() => openGroupDetail(purchase)}
              >
                {visibleColumns.type && (
                  <div className="purchase-header">
                    {/* Gruplu görünüm - metal türleri */}
                    {purchase.metal_types?.includes('gold') && <span className="metal-badge gold">Altın</span>}
                    {purchase.metal_types?.includes('silver') && <span className="metal-badge silver">Gümüş</span>}
                    {!purchase.metal_types && getMetalBadge(purchase.metal_type)}
                    <span className="purchase-id">#{purchase.store_scrap_number || purchase.first_id || purchase.id}</span>
                    {purchase.item_count > 1 && (
                      <span className="item-count-badge">{purchase.item_count} kalem</span>
                    )}
                  </div>
                )}

                {visibleColumns.customer && (
                  <div className="purchase-customer">
                    <div className="customer-avatar">
                      {purchase.customer_name?.charAt(0) || '?'}
                    </div>
                    <div className="customer-details">
                      <span className="name">{purchase.customer_name}</span>
                      <span className="phone">{purchase.customer_phone || '-'}</span>
                    </div>
                  </div>
                )}

                {visibleColumns.purity && (() => {
                  const milyem = getMilyemForPurchase(purchase);
                  return (
                    <div>
                      <span className="cell-value">
                        {purchase.purities || purchase.purity} Ayar{milyem ? ` (${milyem} Milyem)` : ''}
                      </span>
                      <span className="cell-label">{Number(purchase.total_gross_weight || purchase.gross_weight || 0).toFixed(2)} gr brüt</span>
                    </div>
                  );
                })()}

                {visibleColumns.pureWeight && (
                  <div>
                    <span className="cell-value highlight">{Number(purchase.total_pure_weight || purchase.pure_weight || 0).toFixed(2)} gr</span>
                    <span className="cell-label">Has toplam</span>
                  </div>
                )}

                {visibleColumns.unitPrice && (
                  <div>
                    <span className="cell-value">
                      {purchase.item_count > 1
                        ? (purchase.min_unit_price && purchase.max_unit_price && purchase.min_unit_price !== purchase.max_unit_price
                            ? `₺${Number(purchase.min_unit_price).toLocaleString('tr-TR', { minimumFractionDigits: 0 })} - ₺${Number(purchase.max_unit_price).toLocaleString('tr-TR', { minimumFractionDigits: 0 })}`
                            : `₺${Number(purchase.unit_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`)
                        : `₺${Number(purchase.unit_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`}
                    </span>
                    <span className="cell-label">Birim Fiyat</span>
                  </div>
                )}

                {visibleColumns.total && (
                  <div>
                    <span className="cell-value price">₺{Number(purchase.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                  </div>
                )}

                {visibleColumns.paymentMethod && (
                  <div>
                    <span className="cell-value">
                      {purchase.payment_method || '-'}
                      {(() => {
                        // Havale ödemesi varsa banka hesap bilgisini göster
                        if (purchase.payment_method === 'Havale' || purchase.payment_method === 'Karışık') {
                          try {
                            const details = purchase.payment_details
                              ? (typeof purchase.payment_details === 'string' ? JSON.parse(purchase.payment_details) : purchase.payment_details)
                              : [];
                            const havalePayment = Array.isArray(details) ? details.find(p => p.method === 'Havale' && p.bankAccountName) : null;
                            if (havalePayment?.bankAccountName) {
                              return <span style={{ color: '#6366f1', display: 'block', fontSize: '0.75rem' }}>({havalePayment.bankAccountName})</span>;
                            }
                          } catch(e) {}
                        }
                        return null;
                      })()}
                    </span>
                  </div>
                )}

                {visibleColumns.staff && (
                  <div>
                    <span className="cell-value">{purchase.staff_name || '-'}</span>
                  </div>
                )}

                {visibleColumns.date && (
                  <div>
                    <span className="cell-value">{new Date(purchase.created_at).toLocaleDateString('tr-TR')}</span>
                    <span className="cell-label">{new Date(purchase.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}</span>
                  </div>
                )}

                {visibleColumns.notes && (
                  <div>
                    <span className="cell-value notes">{purchase.item_count > 1 ? `${purchase.item_count} kalem hurda` : (purchase.notes || '-')}</span>
                  </div>
                )}

                <div className="purchase-actions">
                  <button className="btn-icon print" onClick={(e) => { e.stopPropagation(); reprintScrapReceipt(purchase); }} title="Fiş Yazdır">
                    <Printer size={14} />
                  </button>
                  {user?.role === 'admin' && (
                    <>
                      <button className="btn-icon edit" onClick={(e) => { e.stopPropagation(); openEditModal(purchase); }} title="Düzenle">
                        <Pencil size={14} />
                      </button>
                      <button className="btn-icon delete" onClick={(e) => { e.stopPropagation(); handleDelete(purchase); }} title="Sil">
                        <Trash2 size={14} />
                      </button>
                    </>
                  )}
                </div>
              </div>
            ))}
            </div>
          </div>
        )}
      </div>
      </>)}

      {/* Modal */}
      {showModal && (
        <div className={`modal-overlay ${closingModal === 'main' ? 'closing' : ''}`}>
          <div className="modal scrap-form-modal large" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2
                onMouseDown={startAnonymousScrapHold}
                onMouseUp={cancelAnonymousScrapHold}
                onMouseLeave={cancelAnonymousScrapHold}
                onTouchStart={startAnonymousScrapHold}
                onTouchEnd={cancelAnonymousScrapHold}
                onTouchCancel={cancelAnonymousScrapHold}
                style={{ userSelect: 'none' }}
              >
                <Scale size={20} style={{ marginRight: '8px' }} /> {editingPurchase ? 'Hurda Alımı Düzenle' : 'Yeni Hurda Alımı'}
              </h2>
              <button className="close-btn" onClick={() => handleCloseModal('main', setShowModal)}>✕</button>
            </div>
            <div className="modal-body scrap-form-body">
              {/* Müşteri Bilgileri */}
              <div className="form-section">
                <h3>Müşteri Bilgileri</h3>

                {/* Müşteri Modu Seçici */}
                <div className="customer-mode-tabs">
                  <button
                    className={`mode-tab ${customerMode === 'select' ? 'active' : ''}`}
                    onClick={() => {
                      setCustomerMode('select');
                      setFormData({...formData, customer_id: '', customer_name: '', customer_phone: ''});
                    }}
                  >
                    Mevcut Müşteri
                  </button>
                  <button
                    className={`mode-tab ${customerMode === 'new' ? 'active' : ''}`}
                    onClick={() => {
                      setCustomerMode('new');
                      setFormData({...formData, customer_id: '', customer_name: '', customer_phone: ''});
                      setCustomerSearch('');
                    }}
                  >
                    Yeni Müşteri
                  </button>
                </div>

                {/* Mevcut Müşteri Seçimi */}
                {customerMode === 'select' && (
                  <div className="customer-select-container">
                    <div className="customer-search-wrapper">
                      {/* Seçili müşteri varsa badge göster */}
                      {formData.customer_id ? (
                        <div className="selected-customer-display">
                          <div className="selected-customer-badge">
                            <span>{formData.customer_name}</span>
                            {formData.customer_phone && <span className="customer-phone-badge">({formData.customer_phone})</span>}
                          </div>
                          <button
                            className="change-customer-btn"
                            onClick={() => {
                              setFormData({...formData, customer_id: '', customer_name: '', customer_phone: ''});
                              setCustomerSearch('');
                              setShowCustomerDropdown(true);
                            }}
                          >
                            Müşteri Değiştir
                          </button>
                        </div>
                      ) : (
                        <>
                          <input
                            type="text"
                            placeholder="Müşteri ara (isim veya telefon)..."
                            value={customerSearch}
                            onChange={(e) => {
                              const value = e.target.value;
                              setCustomerSearch(value);
                              setShowCustomerDropdown(true);
                              // 2+ karakter olduğunda backend'den ara
                              if (value.length >= 2) {
                                searchCustomers(value);
                              }
                            }}
                            onFocus={() => setShowCustomerDropdown(true)}
                            className="customer-search-input"
                          />
                          {showCustomerDropdown && customerSearch && (
                            <div className="customer-dropdown">
                              {filteredCustomers.length > 0 ? (
                                filteredCustomers.map(c => (
                                  <div
                                    key={c.id}
                                    className="customer-option"
                                    onClick={() => {
                                      selectCustomer(c);
                                      setCustomerSearch('');
                                      setShowCustomerDropdown(false);
                                    }}
                                  >
                                    <span className="customer-name">{c.name}</span>
                                    {c.phone && <span className="customer-phone">{c.phone}</span>}
                                  </div>
                                ))
                              ) : (
                                <div className="no-customers">
                                  Müşteri bulunamadı.
                                  <button onClick={() => setCustomerMode('new')}>Yeni oluştur</button>
                                </div>
                              )}
                            </div>
                          )}
                        </>
                      )}
                    </div>
                  </div>
                )}

                {/* Yeni Müşteri Oluşturma */}
                {customerMode === 'new' && (
                  <div className="new-customer-form">
                    <div className="form-row">
                      <div className="form-group">
                        <label>Müşteri Adı *</label>
                        <input
                          type="text"
                          placeholder="Müşteri adı..."
                          value={formData.customer_name}
                          onChange={(e) => setFormData({...formData, customer_name: e.target.value})}
                        />
                      </div>
                      <div className="form-group">
                        <label>Telefon</label>
                        <input
                          type="tel"
                          placeholder="0 532 XXX XX XX"
                          value={formatPhoneDisplay(formData.customer_phone)}
                          onChange={(e) => {
                            const digits = normalizePhoneDigits(e.target.value);
                            setFormData({...formData, customer_phone: digits});
                            checkPhoneNumber(digits);
                          }}
                          className={matchingCustomer ? 'input-warning' : ''}
                        />
                      </div>
                    </div>
                    {/* Eşleşen müşteri uyarısı */}
                    {matchingCustomer && (
                      <div className="matching-customer-alert">
                        <div className="alert-content">
                          <span className="alert-icon">⚠️</span>
                          <span className="alert-text">
                            Bu telefon numarası <strong>{matchingCustomer.name}</strong> adlı müşteriye ait!
                          </span>
                        </div>
                        <button
                          type="button"
                          className="btn-use-existing"
                          onClick={selectMatchingCustomer}
                        >
                          Bu Müşteriyi Seç
                        </button>
                      </div>
                    )}
                    {!matchingCustomer && (
                      <p className="new-customer-info">Bu müşteri otomatik olarak müşteri listesine kaydedilecek.</p>
                    )}
                  </div>
                )}

                {/* Kayıtsız Müşteri Bilgisi */}
                {customerMode === 'anonymous' && (
                  <div className="anonymous-customer-info">
                    <div className="anonymous-badge">
                      <span className="anonymous-icon">👤</span>
                      <div className="anonymous-text">
                        <strong>Kayıtsız Müşteri</strong>
                        <span>Müşteri bilgisi kaydedilmeden hurda alımı yapılacak</span>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              {/* Satış Personeli */}
              <div className="form-section">
                <h3>Satış Personeli</h3>
                <div className="form-group">
                  <label>Personel Seçin</label>
                  <select
                    value={formData.staff_id}
                    onChange={(e) => {
                      const staff = salesStaff.find(s => s.id === parseInt(e.target.value));
                      if (staff) {
                        selectStaff(staff);
                      } else {
                        setFormData({...formData, staff_id: '', staff_name: ''});
                      }
                    }}
                  >
                    <option value="">-- Personel Seçin --</option>
                    {salesStaff.map(staff => (
                      <option key={staff.id} value={staff.id}>{staff.name}</option>
                    ))}
                  </select>
                </div>
              </div>

              {/* Metal Türü Seçimi (Gümüş mağazalarda Altın/Gümüş toggle) */}
              {!isGoldStore && !editingPurchase && (
                <div style={{ marginBottom: '12px' }}>
                  <label className="section-label">Metal Türü</label>
                  <div style={{ display: 'flex', gap: '8px' }}>
                    <button
                      type="button"
                      onClick={() => handleMetalTypeChange('gold')}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '8px', border: '2px solid',
                        borderColor: formData.metal_type === 'gold' ? '#f59e0b' : '#d1d5db',
                        background: formData.metal_type === 'gold' ? 'linear-gradient(135deg, #fef3c7, #fde68a)' : '#f9fafb',
                        color: formData.metal_type === 'gold' ? '#92400e' : '#6b7280',
                        fontWeight: formData.metal_type === 'gold' ? '700' : '500',
                        cursor: 'pointer', fontSize: '0.9rem'
                      }}
                    >
                      🥇 Altın Hurdası
                    </button>
                    <button
                      type="button"
                      onClick={() => handleMetalTypeChange('silver')}
                      style={{
                        flex: 1, padding: '10px', borderRadius: '8px', border: '2px solid',
                        borderColor: formData.metal_type === 'silver' ? '#9ca3af' : '#d1d5db',
                        background: formData.metal_type === 'silver' ? 'linear-gradient(135deg, #f3f4f6, #e5e7eb)' : '#f9fafb',
                        color: formData.metal_type === 'silver' ? '#374151' : '#6b7280',
                        fontWeight: formData.metal_type === 'silver' ? '700' : '500',
                        cursor: 'pointer', fontSize: '0.9rem'
                      }}
                    >
                      🥈 Gümüş Hurdası
                    </button>
                  </div>
                </div>
              )}

              {/* Hızlı Hurda Kartları (Altın seçili olduğunda ve yeni kayıt) */}
              {(isGoldStore || formData.metal_type === 'gold') && !editingPurchase && (
                <div className="quick-scrap-section">
                  <label className="section-label">Hızlı Ekle (Tıkla ekle)</label>
                  <div className="quick-scrap-cards">
                    {quickScrapOptions.map(option => {
                      const cartItem = scrapCart.find(item => item.name === option.name && item.isQuickOption);
                      const quantity = cartItem?.quantity || 0;
                      return (
                        <button
                          key={option.id}
                          type="button"
                          className={`quick-scrap-card ${quantity > 0 ? 'in-cart' : ''}`}
                          onClick={() => handleQuickScrapSelect(option)}
                        >
                          <Coins size={20} style={{ color: '#f59e0b' }} />
                          <span className="card-name">{option.name}</span>
                          <span className="card-weight">{option.weight} gr</span>
                          {quantity > 0 && <span className="card-qty-badge">{quantity}</span>}
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}

              {/* Manuel Hurda Ekleme */}
              <div className="manual-scrap-section">
                <label className="section-label">{editingPurchase ? 'Hurda Bilgileri' : 'Manuel Ekle'}</label>
                <div className="scrap-form-grid-inline">
                  <div className="form-field ayar-field">
                    <label>Ayar</label>
                    <div className="ayar-select-wrapper">
                      <select
                        value={formData.purity}
                        onChange={(e) => {
                          setFormData({...formData, purity: e.target.value, custom_purity_rate: null});
                          setIsEditingPurityRate(false);
                        }}
                      >
                        {formData.metal_type === 'gold' ? (
                          <>
                            <option value="24">24 Ayar</option>
                            <option value="22">22 Ayar</option>
                            <option value="21">21 Ayar</option>
                            <option value="18">18 Ayar</option>
                            <option value="14">14 Ayar</option>
                            <option value="10">10 Ayar</option>
                            <option value="9">9 Ayar</option>
                            <option value="8">8 Ayar</option>
                          </>
                        ) : (
                          <>
                            <option value="999">999 Ayar</option>
                            <option value="925">925 Ayar</option>
                            <option value="900">900 Ayar</option>
                            <option value="850">850 Ayar</option>
                            <option value="800">800 Ayar</option>
                            <option value="750">750 Ayar</option>
                            <option value="700">700 Ayar</option>
                          </>
                        )}
                      </select>
                      <span className="purity-rate-label">Oran:</span>
                      {isEditingPurityRate ? (
                        <input
                          type="number"
                          className="purity-rate-input"
                          value={editingPurityRateValue}
                          onChange={(e) => setEditingPurityRateValue(e.target.value)}
                          onBlur={() => {
                            updatePurityRate(formData.metal_type, formData.purity, editingPurityRateValue);
                            setIsEditingPurityRate(false);
                          }}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') {
                              updatePurityRate(formData.metal_type, formData.purity, editingPurityRateValue);
                              setIsEditingPurityRate(false);
                            }
                            if (e.key === 'Escape') setIsEditingPurityRate(false);
                          }}
                          autoFocus
                        />
                      ) : (
                        <span
                          className="purity-rate-display"
                          onDoubleClick={() => {
                            const currentRate = formData.custom_purity_rate || purityRates[formData.metal_type]?.[formData.purity] || defaultPurityRates[formData.metal_type]?.[formData.purity] || 0;
                            setEditingPurityRateValue(Math.round(currentRate * 1000).toString());
                            setIsEditingPurityRate(true);
                          }}
                          title="Çift tıkla düzenle"
                        >
                          {Math.round((formData.custom_purity_rate || purityRates[formData.metal_type]?.[formData.purity] || defaultPurityRates[formData.metal_type]?.[formData.purity] || 0) * 1000)}
                        </span>
                      )}
                    </div>
                  </div>
                  <div className="form-field">
                    <label>Brüt Gram</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.gross_weight}
                      onChange={(e) => setFormData({...formData, gross_weight: e.target.value})}
                    />
                  </div>
                  <div className="form-field">
                    <label>Fire (gr)</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="0.00"
                      value={formData.waste_gram}
                      onChange={(e) => setFormData({...formData, waste_gram: e.target.value})}
                    />
                  </div>
                  <div className="form-field">
                    <label>Net Gram</label>
                    <input
                      type="text"
                      readOnly
                      value={((parseFloat(formData.gross_weight) || 0) - (parseFloat(formData.waste_gram) || 0)).toFixed(2)}
                      style={{ backgroundColor: '#f0fdf4', fontWeight: '600', color: '#16a34a', cursor: 'default' }}
                    />
                  </div>
                  <div className="form-field">
                    <label>Has Gr Fiyatı (₺) <span className="required">*</span></label>
                    <PriceInput
                      placeholder="0.00"
                      value={formData.unit_price}
                      onChange={(val) => setFormData({...formData, unit_price: val})}
                      className={!formData.unit_price ? 'input-warning' : ''}
                    />
                    {!formData.unit_price && (
                      <span className="field-hint warning">Birim fiyat zorunludur</span>
                    )}
                  </div>
                  <div className="form-field">
                    <label>Yüzdelik %</label>
                    <input
                      type="number"
                      step="1"
                      min="0"
                      max={(() => {
                        if (user?.role === 'admin') return 100;
                        const staff = salesStaff.find(s => s.id === parseInt(formData.staff_id));
                        const limit = staff?.max_scrap_bonus_percent;
                        return limit !== null && limit !== undefined ? limit : 100;
                      })()}
                      placeholder=""
                      value={formData.bonus_percent}
                      onChange={(e) => {
                        let val = e.target.value;
                        if (user?.role !== 'admin') {
                          const staff = salesStaff.find(s => s.id === parseInt(formData.staff_id));
                          const limit = staff?.max_scrap_bonus_percent;
                          if (limit !== null && limit !== undefined && parseFloat(val) > limit) {
                            toast.warning(`Hurda yüzdelik limiti %${limit}'dir. Bu limiti aşamazsınız.`, { toastId: 'scrap-bonus-limit' });
                            val = String(limit);
                          }
                        }
                        setFormData({...formData, bonus_percent: val});
                      }}
                      title="Müşteri ürün alırsa uygulanacak indirim yüzdesi"
                    />
                  </div>
                  <div className="form-field wide">
                    <label>Açıklama</label>
                    <input
                      type="text"
                      placeholder="Opsiyonel"
                      value={formData.notes}
                      onChange={(e) => setFormData({...formData, notes: e.target.value})}
                    />
                  </div>
                  {!editingPurchase && (
                    <div className="form-field btn-field">
                      <button
                        type="button"
                        className="btn-add-scrap"
                        onClick={addScrapToCart}
                        disabled={!formData.gross_weight || !formData.unit_price}
                      >
                        <Plus size={16} /> Sepete Ekle
                      </button>
                    </div>
                  )}
                </div>

                {/* Düzenleme modunda hesaplama özeti göster */}
                {editingPurchase && (
                  <div className="calculation-summary">
                    <div className="calc-row">
                      <span>Has Gram:</span>
                      <span className="calc-value">{calculatePureWeight()} gr</span>
                    </div>
                    <div className="calc-row total">
                      <span>Toplam Tutar:</span>
                      {isEditingTotal ? (
                        <PriceInput
                          className="edit-total-input"
                          value={editingTotalValue}
                          onChange={(val) => setEditingTotalValue(val)}
                          onBlur={finishEditingTotal}
                          onKeyDown={(e) => {
                            if (e.key === 'Enter') finishEditingTotal();
                            if (e.key === 'Escape') cancelEditingTotal();
                          }}
                          autoFocus
                        />
                      ) : (
                        <span
                          className="calc-value editable-total"
                          onDoubleClick={startEditingTotal}
                          title="Çift tıkla düzenle"
                          style={{ cursor: 'pointer' }}
                        >
                          ₺{Number(calculateTotal()).toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          {customTotal !== null && <span className="manual-badge">Manuel</span>}
                        </span>
                      )}
                    </div>
                  </div>
                )}
              </div>

              {/* Düzenleme modunda tarih/saat */}
              {editingPurchase && (
                <div className="edit-datetime-section" style={{ marginBottom: '16px' }}>
                  <label className="section-label"><Calendar size={16} style={{ marginRight: '6px' }} /> İşlem Tarihi ve Saati</label>
                  <input
                    type="datetime-local"
                    value={formData.created_at}
                    onChange={(e) => setFormData({...formData, created_at: e.target.value})}
                    style={{
                      width: '100%',
                      padding: '10px 12px',
                      border: '1px solid #ddd',
                      borderRadius: '8px',
                      fontSize: '14px',
                      marginTop: '8px'
                    }}
                  />
                </div>
              )}

              {/* Düzenleme modunda ödeme bilgileri düzenleme */}
              {editingPurchase && (
                <div className="edit-payment-section">
                  <label className="section-label"><Banknote size={16} style={{ marginRight: '6px' }} /> Ödeme Bilgileri</label>

                  {/* Mevcut Ödemeler Listesi */}
                  {scrapPayments.length > 0 && (
                    <div className="edit-payments-list">
                      {scrapPayments.map((payment, idx) => (
                        <div key={payment.id || idx} className="edit-payment-item">
                          <div className="edit-payment-info">
                            <span className="payment-method-badge">{payment.method || '-'}</span>
                            {(payment.bankAccountName || payment.bank_account_name) && (
                              <span className="bank-name-tag">({payment.bankAccountName || payment.bank_account_name})</span>
                            )}
                            {payment.currency && payment.currency !== 'TRY' && (
                              <span className="foreign-amount">
                                {payment.currency === 'USD' ? '$' : payment.currency === 'EUR' ? '€' : payment.currency === 'GOLD' ? 'gr Au ' : payment.currency === 'SILVER' ? 'gr Ag ' : ''}{payment.amount || 0}
                              </span>
                            )}
                            <span className="payment-amount-tl">₺{Number(payment.amountTL || payment.amount_tl || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</span>
                          </div>
                          <button
                            className="remove-payment-btn"
                            onClick={() => removeScrapPayment(payment.id)}
                            title="Ödemeyi Sil"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      ))}
                      <div className="edit-payments-total">
                        <span>Toplam Ödenen:</span>
                        <strong>₺{scrapPayments.reduce((sum, p) => sum + (p.amountTL || p.amount_tl || 0), 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</strong>
                      </div>
                    </div>
                  )}

                  {/* Yeni Ödeme Ekleme */}
                  <div className="add-payment-form">
                    <div className="payment-form-row">
                      <div className="currency-selector compact">
                        <button
                          type="button"
                          className={`currency-btn ${selectedPaymentCurrency === 'TRY' ? 'active' : ''}`}
                          onClick={() => { setSelectedPaymentCurrency('TRY'); setPaymentAmount(''); }}
                        >₺ TL</button>
                        <button
                          type="button"
                          className={`currency-btn ${selectedPaymentCurrency === 'USD' ? 'active' : ''}`}
                          onClick={() => { setSelectedPaymentCurrency('USD'); setPaymentAmount(''); }}
                        >$ USD</button>
                        <button
                          type="button"
                          className={`currency-btn ${selectedPaymentCurrency === 'EUR' ? 'active' : ''}`}
                          onClick={() => { setSelectedPaymentCurrency('EUR'); setPaymentAmount(''); }}
                        >€ EUR</button>
                      </div>
                      <PriceInput
                        className="payment-amount-input"
                        placeholder="Tutar"
                        value={paymentAmount}
                        onChange={(val) => setPaymentAmount(val)}
                      />
                    </div>
                    {selectedPaymentCurrency !== 'TRY' && paymentAmount && Number(paymentAmount) > 0 && (
                      <div className="tl-equivalent-small">
                        = ₺{((Number(paymentAmount) || 0) * getPaymentExchangeRate(selectedPaymentCurrency)).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                      </div>
                    )}
                    <div className="payment-method-buttons compact">
                      <button
                        type="button"
                        className="method-btn cash"
                        onClick={() => addScrapPayment('Nakit')}
                        disabled={!paymentAmount || Number(paymentAmount) <= 0}
                      >
                        <Banknote size={14} /> Nakit
                      </button>
                      <button
                        type="button"
                        className="method-btn transfer"
                        onClick={() => {
                          if (!paymentAmount || Number(paymentAmount) <= 0) {
                            toast.warning('Önce tutar girin!');
                            return;
                          }
                          setShowBankModal(true);
                        }}
                        disabled={!paymentAmount || Number(paymentAmount) <= 0}
                      >
                        <Building2 size={14} /> Havale
                      </button>
                      <button
                        type="button"
                        className={`method-btn credit ${showPaymentCreditOptions ? 'active' : ''}`}
                        onClick={() => setShowPaymentCreditOptions(!showPaymentCreditOptions)}
                        disabled={!paymentAmount || Number(paymentAmount) <= 0}
                      >
                        <ClipboardList size={14} /> Açık Hesap
                      </button>
                    </div>
                    {/* Açık Hesap Seçenekleri */}
                    {showPaymentCreditOptions && paymentAmount && Number(paymentAmount) > 0 && (
                      <div className="credit-options compact">
                        <div className="credit-option" onClick={() => {
                          const currentAmountTL = selectedPaymentCurrency === 'TRY'
                            ? Number(paymentAmount) || 0
                            : (Number(paymentAmount) || 0) * getPaymentExchangeRate(selectedPaymentCurrency);
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (TL)',
                            currency: 'TRY',
                            amount: currentAmountTL,
                            amountTL: currentAmountTL,
                            creditType: 'TL',
                            rate: 1
                          };
                          setScrapPayments([...scrapPayments, newPayment]);
                          setPaymentAmount('');
                          setShowPaymentCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">₺</span>
                          <span>TL</span>
                        </div>
                        <div className="credit-option" onClick={() => {
                          const currentAmountTL = selectedPaymentCurrency === 'TRY'
                            ? Number(paymentAmount) || 0
                            : (Number(paymentAmount) || 0) * getPaymentExchangeRate(selectedPaymentCurrency);
                          const goldPrice = getPaymentExchangeRate('GOLD');
                          const goldGram = goldPrice > 0 ? (currentAmountTL / goldPrice).toFixed(3) : 0;
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (Gram)',
                            currency: 'GOLD',
                            amount: parseFloat(goldGram),
                            amountTL: currentAmountTL,
                            creditType: 'GRAM',
                            goldRate: goldPrice,
                            rate: goldPrice
                          };
                          setScrapPayments([...scrapPayments, newPayment]);
                          setPaymentAmount('');
                          setShowPaymentCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">Au</span>
                          <span>Gram Altın</span>
                        </div>
                        <div className="credit-option" onClick={() => {
                          const currentAmountTL = selectedPaymentCurrency === 'TRY'
                            ? Number(paymentAmount) || 0
                            : (Number(paymentAmount) || 0) * getPaymentExchangeRate(selectedPaymentCurrency);
                          const silverPrice = getPaymentExchangeRate('SILVER');
                          const silverGram = silverPrice > 0 ? (currentAmountTL / silverPrice).toFixed(2) : 0;
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (Gümüş)',
                            currency: 'SILVER',
                            amount: parseFloat(silverGram),
                            amountTL: currentAmountTL,
                            creditType: 'GUMUS',
                            silverRate: silverPrice,
                            rate: silverPrice
                          };
                          setScrapPayments([...scrapPayments, newPayment]);
                          setPaymentAmount('');
                          setShowPaymentCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">Ag</span>
                          <span>Gram Gümüş</span>
                        </div>
                      </div>
                    )}
                  </div>
                </div>
              )}

              {/* Hurda Sepeti */}
              {!editingPurchase && (
                <div className="scrap-cart-section">
                  <div className="section-header">
                    <label className="section-label">Hurda Sepeti ({scrapCart.length} kalem)</label>
                    {scrapCart.length > 0 && (
                      <button className="btn-clear-cart" onClick={clearScrapCart}>
                        <Trash2 size={14} /> Temizle
                      </button>
                    )}
                  </div>
                  {scrapCart.length === 0 ? (
                    <div className="scrap-cart-empty">
                      <Scale size={32} style={{ opacity: 0.3 }} />
                      <span>Henüz hurda eklenmedi</span>
                    </div>
                  ) : (
                    <div className="scrap-cart-list">
                      {scrapCart.map(item => (
                        <div key={item.id} className="scrap-cart-item">
                          <div className="scrap-item-info">
                            <span className="scrap-item-name">{item.name}</span>
                            <span className="scrap-item-details">
                              {item.purity} Ayar{item.purity_rate ? ` (${Math.round(item.purity_rate * 1000)} Milyem)` : ''} • Brüt: {item.gross_weight.toFixed(2)}gr{item.waste_gram > 0 ? ` • Fire: ${item.waste_gram.toFixed(2)}gr • Net: ${(item.gross_weight - item.waste_gram).toFixed(2)}gr` : ''} • Has: {item.pure_weight.toFixed(2)}gr • ₺{item.unit_price.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}/gr
                            </span>
                          </div>
                          <div className="scrap-item-qty">
                            <button onClick={() => updateScrapQuantity(item.id, -1)} disabled={item.quantity <= 1}>-</button>
                            <span
                              className="scrap-qty-value"
                              title="Çift tıkla düzenle"
                              onDoubleClick={(e) => {
                                const span = e.currentTarget;
                                const input = document.createElement('input');
                                input.type = 'number';
                                input.min = '1';
                                input.value = item.quantity;
                                input.className = 'scrap-qty-input';
                                input.style.cssText = 'width:40px;text-align:center;font-size:inherit;font-weight:inherit;border:1px solid #3b82f6;border-radius:4px;padding:2px;outline:none;';
                                let committed = false;
                                const commit = () => {
                                  if (committed) return;
                                  committed = true;
                                  const val = parseInt(input.value) || 1;
                                  setScrapQuantity(item.id, val);
                                  if (input.parentNode) input.parentNode.replaceChild(span, input);
                                };
                                input.onblur = commit;
                                input.onkeydown = (ev) => {
                                  if (ev.key === 'Enter') { ev.preventDefault(); commit(); }
                                  if (ev.key === 'Escape') { committed = true; if (input.parentNode) input.parentNode.replaceChild(span, input); }
                                };
                                span.parentNode.replaceChild(input, span);
                                input.focus();
                                input.select();
                              }}
                            >{item.quantity}</span>
                            <button onClick={() => updateScrapQuantity(item.id, 1)}>+</button>
                          </div>
                          <div className="scrap-item-total">
                            ₺{item.total.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                          </div>
                          <button className="scrap-item-remove" onClick={() => removeScrapFromCart(item.id)}>
                            <X size={16} />
                          </button>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {/* Toplam Özet */}
              {!editingPurchase && scrapCart.length > 0 && (
                <div className="scrap-summary-section">
                  <div className="scrap-total-row">
                    <span>Toplam Hurda Tutarı:</span>
                    {isEditingTotal ? (
                      <PriceInput
                        className="edit-total-input"
                        value={editingTotalValue}
                        onChange={(val) => setEditingTotalValue(val)}
                        onBlur={() => {
                          const newValue = parseFloat(editingTotalValue);
                          if (!isNaN(newValue) && newValue >= 0) {
                            setCustomTotal(newValue);
                          }
                          setIsEditingTotal(false);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const newValue = parseFloat(editingTotalValue);
                            if (!isNaN(newValue) && newValue >= 0) {
                              setCustomTotal(newValue);
                            }
                            setIsEditingTotal(false);
                          }
                          if (e.key === 'Escape') {
                            setIsEditingTotal(false);
                            setCustomTotal(null);
                          }
                        }}
                        autoFocus
                      />
                    ) : (
                      <span
                        className="scrap-total-amount editable-total"
                        onDoubleClick={() => {
                          setEditingTotalValue(scrapCartTotal.toFixed(2));
                          setIsEditingTotal(true);
                        }}
                        title="Çift tıkla düzenle"
                        style={{ cursor: 'pointer' }}
                      >
                        ₺{scrapCartTotal.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        {customTotal !== null && <span className="manual-badge">Manuel</span>}
                      </span>
                    )}
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowModal(false)}>İptal</button>
              <button
                className="btn-save scrap-apply"
                onClick={handleSubmit}
                disabled={!editingPurchase && scrapCart.length === 0}
              >
                <Scale size={16} /> {editingPurchase ? 'Güncelle' : `Kaydet (${scrapCart.length} kalem)`}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Yardım Modal */}
      {showHelpModal && (
        <div className={`modal-overlay ${closingModal === 'help' ? 'closing' : ''}`}>
          <div className="modal help-modal" onClick={e => e.stopPropagation()}>
            <div className="modal-header help-header">
              <h2><BookOpen size={20} style={{ marginRight: '8px' }} /> Hurda Alımı Yardım</h2>
              <button className="close-btn" onClick={() => handleCloseModal('help', setShowHelpModal)}>✕</button>
            </div>
            <div className="help-modal-content">
              {/* Genel Bilgi */}
              <div className="help-section">
                <h3><Scale size={18} style={{ marginRight: '6px' }} /> Genel Bilgi</h3>
                <p>Bu sayfa üzerinden müşterilerden hurda altın ve gümüş alımı yapabilirsiniz. Sistem otomatik olarak has gram ve toplam tutar hesaplar.</p>
              </div>

              {/* Yeni Alım */}
              <div className="help-section">
                <h3><Plus size={18} style={{ marginRight: '6px' }} /> Yeni Hurda Alımı</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span><strong>+ Yeni Alım</strong> butonuna tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Müşteri seçin veya yeni oluşturun</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Metal türü (Altın/Gümüş) ve ayarı seçin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span>Brüt gram ve fire gramajını girin</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">5</span>
                    <span>Birim fiyatı ayarlayın (güncel fiyat otomatik gelir)</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">6</span>
                    <span><strong>Kaydet</strong> butonuna tıklayın</span>
                  </div>
                </div>
              </div>

              {/* Hesaplama */}
              <div className="help-section">
                <h3><Calculator size={18} style={{ marginRight: '6px' }} /> Has Gram Hesaplama</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span>Brüt gramdan fire gramajı düşülür</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Kalan net gram, ayar katsayısı ile çarpılır</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Sonuç: Has gram (saf altın/gümüş miktarı)</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Örnek: 10 gr brüt, 22 ayar, 0.5 gr fire = (10 - 0.5) × 0.916 = 8.70 gr has</div>
              </div>

              {/* Ayar Katsayıları */}
              <div className="help-section">
                <h3><BarChart3 size={18} style={{ marginRight: '6px' }} /> Ayar Katsayıları</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num"><Coins size={16} style={{ color: '#f59e0b' }} /></span>
                    <span><strong>Altın:</strong> 24K: 0.995 | 22K: 0.916 | 18K: 0.750 | 14K: 0.585</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num"><Coins size={16} style={{ color: '#9ca3af' }} /></span>
                    <span><strong>Gümüş:</strong> 999: 0.999 | 925: 0.925 | 900: 0.900</span>
                  </div>
                </div>
              </div>

              {/* Özel Kur */}
              <div className="help-section">
                <h3><Coins size={18} style={{ marginRight: '6px' }} /> Özel Kur Ayarlama</h3>
                <div className="help-steps">
                  <div className="help-step">
                    <span className="step-num">1</span>
                    <span>Sayfanın üstündeki altın/gümüş fiyat kartına tıklayın</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">2</span>
                    <span>Açılan modalda <strong>anlık piyasa kuru</strong> ve varsa <strong>kayıtlı kurlar</strong> görüntülenir</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">3</span>
                    <span>Alış ve satış kurlarını manuel girerek özel fiyat belirleyebilirsiniz</span>
                  </div>
                  <div className="help-step">
                    <span className="step-num">4</span>
                    <span><strong>Kaydet</strong> ile kur kaydedilir, işlemlerde bu kur kullanılır</span>
                  </div>
                </div>
                <div className="help-tip"><Lightbulb size={16} style={{ marginRight: '6px', color: '#f59e0b' }} /> Özel kur silerseniz sistem tekrar otomatik piyasa kurunu kullanır.</div>
              </div>

              {/* Butonlar */}
              <div className="help-section">
                <h3><CircleDot size={18} style={{ marginRight: '6px' }} /> Buton Açıklamaları</h3>
                <div className="help-buttons-grid">
                  <div className="help-button-item">
                    <span className="btn-icon"><Plus size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Yeni Alım</strong>
                      <p>Yeni hurda alım kaydı oluşturur</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><ScrollText size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>İşlem Geçmişi</strong>
                      <p>Tüm işlem loglarını gösterir</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Pencil size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Düzenle</strong>
                      <p>Kayıt bilgilerini günceller</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Trash2 size={20} style={{ color: '#ef4444' }} /></span>
                    <div>
                      <strong>Sil</strong>
                      <p>Kaydı sistemden kaldırır</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Settings size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Sütunlar</strong>
                      <p>Görünür sütunları ayarlar</p>
                    </div>
                  </div>
                  <div className="help-button-item">
                    <span className="btn-icon"><Search size={20} style={{ color: '#6366f1' }} /></span>
                    <div>
                      <strong>Filtre</strong>
                      <p>Altın/Gümüş alımlarını filtreler</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* İpuçları */}
              <div className="help-section tips-section">
                <h3><Lightbulb size={18} style={{ marginRight: '6px' }} /> İpuçları</h3>
                <ul className="help-tips-list">
                  <li>Güncel altın/gümüş fiyatları otomatik yüklenir</li>
                  <li>Birim fiyatı manuel değiştirebilirsiniz</li>
                  <li>Fire gramajını doğru girin, sonucu etkiler</li>
                  <li>Yeni müşteri otomatik kaydedilir</li>
                  <li>Personel seçimi yaparak takip kolaylaşır</li>
                  <li>Notlar kısmına önemli detayları yazın</li>
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Özel Kur Modal */}
      {showRateModal && (
        <div className={`modal-overlay ${closingModal === 'rate' ? 'closing' : ''}`}>
          <div className="modal rate-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2>💱 {getRateModalLabel(rateModalCurrency)} Kuru</h2>
              <button className="close-btn" onClick={() => handleCloseModal('rate', setShowRateModal)}>✕</button>
            </div>
            <div className="modal-body">
              <div className="rate-comparison">
                <div className="rate-section">
                  <h3>Anlık Piyasa Kuru (Harem Altın)</h3>
                  <div className="rate-display">
                    <div className="rate-item">
                      <span className="rate-label">Alış</span>
                      <span className="rate-value">
                        {rateModalCurrency === 'SILVER_USD'
                          ? (prices?.gumus_alis && prices?.USD_alis
                              ? (parseFloat(prices.gumus_alis) / parseFloat(prices.USD_alis)).toFixed(2)
                              : '--')
                          : prices?.[getRatePriceKeys(rateModalCurrency).alis]
                        } {rateModalCurrency === 'SILVER_USD' ? '$' : '₺'}
                      </span>
                    </div>
                    <div className="rate-item">
                      <span className="rate-label">Satış</span>
                      <span className="rate-value">
                        {rateModalCurrency === 'SILVER_USD'
                          ? (prices?.gumus_satis && prices?.USD_satis
                              ? (parseFloat(prices.gumus_satis) / parseFloat(prices.USD_satis)).toFixed(2)
                              : '--')
                          : prices?.[getRatePriceKeys(rateModalCurrency).satis]
                        } {rateModalCurrency === 'SILVER_USD' ? '$' : '₺'}
                      </span>
                    </div>
                  </div>
                </div>

                {customRates[rateModalCurrency] && (
                  <div className="rate-section custom">
                    <h3>
                      {(customRates[rateModalCurrency].alis_percentage != null && customRates[rateModalCurrency].satis_percentage != null)
                        ? 'Makas Farkı (Dinamik)'
                        : (customRates[rateModalCurrency].alis_percentage != null || customRates[rateModalCurrency].satis_percentage != null)
                          ? 'Kayıtlı Kurlar (Karışık)'
                          : 'Kayıtlı Kurlar (Sabit)'}
                    </h3>
                    <div className="rate-display">
                      <div className="rate-item">
                        <span className="rate-label">Alış</span>
                        {customRates[rateModalCurrency].alis_percentage != null ? (
                          <span className="rate-value custom" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <span style={{ fontSize: '0.75rem', color: '#f59e0b' }}>-%{customRates[rateModalCurrency].alis_percentage}</span>
                            <span>{getAlisRate(rateModalCurrency).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺</span>
                          </span>
                        ) : (
                          <span className="rate-value custom">{customRates[rateModalCurrency].rate_alis || '-'} ₺</span>
                        )}
                      </div>
                      <div className="rate-item">
                        <span className="rate-label">Satış</span>
                        {customRates[rateModalCurrency].satis_percentage != null ? (
                          <span className="rate-value custom" style={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end' }}>
                            <span style={{ fontSize: '0.75rem', color: '#10b981' }}>+%{customRates[rateModalCurrency].satis_percentage}</span>
                            <span>{getSatisRate(rateModalCurrency).toLocaleString('tr-TR', { maximumFractionDigits: 2 })} ₺</span>
                          </span>
                        ) : (
                          <span className="rate-value custom">{customRates[rateModalCurrency].rate_satis || customRates[rateModalCurrency].rate || '-'} ₺</span>
                        )}
                      </div>
                    </div>
                    <p className="rate-info">
                      Son güncelleme: {new Date(customRates[rateModalCurrency].updated_at).toLocaleString('tr-TR')}
                      {customRates[rateModalCurrency].updated_by_name && ` - ${customRates[rateModalCurrency].updated_by_name}`}
                    </p>
                  </div>
                )}

                <div className="rate-section input">
                  <h3>Özel Kur Gir</h3>

                  {/* ALIŞ BÖLÜMÜ */}
                  <div style={{ marginBottom: '16px', padding: '14px', background: 'rgba(220, 38, 38, 0.04)', borderRadius: '10px', border: '1px solid rgba(220, 38, 38, 0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#dc2626' }}>Alış Kuru</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button type="button" onClick={() => { setRateModalAlisMode('manual'); setRateModalAlisPercentage(''); }}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                            background: rateModalAlisMode === 'manual' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e5e7eb',
                            color: rateModalAlisMode === 'manual' ? 'white' : '#6b7280' }}>
                          Manuel
                        </button>
                        <button type="button" onClick={() => setRateModalAlisMode('percentage')}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                            background: rateModalAlisMode === 'percentage' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : '#e5e7eb',
                            color: rateModalAlisMode === 'percentage' ? 'white' : '#6b7280' }}>
                          Makas Farkı
                        </button>
                      </div>
                    </div>
                    {rateModalAlisMode === 'percentage' ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#dc2626' }}>%</span>
                          <input type="number" step="0.1" value={rateModalAlisPercentage}
                            onChange={(e) => handleAlisPercentageChange(e.target.value)}
                            placeholder="Örn: 2"
                            style={{ flex: 1, padding: '10px 12px', borderRadius: '6px', border: '2px solid #dc2626', fontSize: '1rem', fontWeight: '600', background: '#fef2f2' }} />
                        </div>
                        <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#dc2626' }}>
                          Anlık fiyattan <strong>%{rateModalAlisPercentage || '0'} düşük</strong>
                        </p>
                        {rateModalAlisPercentage && (
                          <div style={{ marginTop: '8px', padding: '6px 10px', background: 'rgba(220,38,38,0.08)', borderRadius: '6px', textAlign: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: '#991b1b' }}>Hesaplanan: </span>
                            <strong style={{ color: '#b91c1c' }}>₺{((() => { const { baseAlis } = getBaseRates(); return (baseAlis * (1 - parseFloat(rateModalAlisPercentage) / 100)).toLocaleString('tr-TR', { maximumFractionDigits: 2 }); })())}</strong>
                            <span style={{ fontSize: '0.65rem', color: '#991b1b', marginLeft: '4px' }}>(Anlık: ₺{getBaseRates().baseAlis?.toLocaleString('tr-TR')})</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <PriceInput value={rateModalAlisValue}
                          onChange={(val) => setRateModalAlisValue(val)}
                          placeholder="Alış kuru (₺)..."
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '2px solid #dc2626', fontSize: '1rem', fontWeight: '600' }} />
                      </div>
                    )}
                  </div>

                  {/* SATIŞ BÖLÜMÜ */}
                  <div style={{ marginBottom: '16px', padding: '14px', background: 'rgba(22, 163, 74, 0.04)', borderRadius: '10px', border: '1px solid rgba(22, 163, 74, 0.15)' }}>
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '10px' }}>
                      <span style={{ fontWeight: '700', fontSize: '0.9rem', color: '#16a34a' }}>Satış Kuru</span>
                      <div style={{ display: 'flex', gap: '4px' }}>
                        <button type="button" onClick={() => { setRateModalSatisMode('manual'); setRateModalSatisPercentage(''); }}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                            background: rateModalSatisMode === 'manual' ? 'linear-gradient(135deg, #3b82f6, #2563eb)' : '#e5e7eb',
                            color: rateModalSatisMode === 'manual' ? 'white' : '#6b7280' }}>
                          Manuel
                        </button>
                        <button type="button" onClick={() => setRateModalSatisMode('percentage')}
                          style={{ padding: '4px 10px', borderRadius: '6px', border: 'none', cursor: 'pointer', fontWeight: '600', fontSize: '0.75rem',
                            background: rateModalSatisMode === 'percentage' ? 'linear-gradient(135deg, #f59e0b, #d97706)' : '#e5e7eb',
                            color: rateModalSatisMode === 'percentage' ? 'white' : '#6b7280' }}>
                          Makas Farkı
                        </button>
                      </div>
                    </div>
                    {rateModalSatisMode === 'percentage' ? (
                      <div>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                          <span style={{ fontWeight: '600', color: '#16a34a' }}>%</span>
                          <input type="number" step="0.1" value={rateModalSatisPercentage}
                            onChange={(e) => handleSatisPercentageChange(e.target.value)}
                            placeholder="Örn: 2"
                            style={{ flex: 1, padding: '10px 12px', borderRadius: '6px', border: '2px solid #16a34a', fontSize: '1rem', fontWeight: '600', background: '#f0fdf4' }} />
                        </div>
                        <p style={{ margin: '4px 0 0', fontSize: '0.7rem', color: '#16a34a' }}>
                          Anlık fiyattan <strong>%{rateModalSatisPercentage || '0'} yüksek</strong>
                        </p>
                        {rateModalSatisPercentage && (
                          <div style={{ marginTop: '8px', padding: '6px 10px', background: 'rgba(22,163,74,0.08)', borderRadius: '6px', textAlign: 'center' }}>
                            <span style={{ fontSize: '0.75rem', color: '#166534' }}>Hesaplanan: </span>
                            <strong style={{ color: '#15803d' }}>₺{((() => { const { baseSatis } = getBaseRates(); return (baseSatis * (1 + parseFloat(rateModalSatisPercentage) / 100)).toLocaleString('tr-TR', { maximumFractionDigits: 2 }); })())}</strong>
                            <span style={{ fontSize: '0.65rem', color: '#166534', marginLeft: '4px' }}>(Anlık: ₺{getBaseRates().baseSatis?.toLocaleString('tr-TR')})</span>
                          </div>
                        )}
                      </div>
                    ) : (
                      <div>
                        <PriceInput value={rateModalSatisValue}
                          onChange={(val) => setRateModalSatisValue(val)}
                          placeholder="Satış kuru (₺)..."
                          style={{ width: '100%', padding: '10px 12px', borderRadius: '6px', border: '2px solid #16a34a', fontSize: '1rem', fontWeight: '600' }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              {customRates[rateModalCurrency] && (
                <button
                  className="btn danger"
                  onClick={() => removeCustomRate(rateModalCurrency)}
                >
                  Kayıtlı Kuru Sil
                </button>
              )}
              <button className="btn secondary" onClick={() => setShowRateModal(false)}>İptal</button>
              <button className="btn primary" onClick={saveRateModal}>Kaydet</button>
            </div>
          </div>
        </div>
      )}

      {/* Ödeme Modalı - RetailSales cash-modal ile aynı */}
      {showPaymentModal && (() => {
        const totalAmount = pendingScrapData?.totalAmount || 0;
        const totalPaid = scrapPayments.reduce((sum, p) => sum + p.amountTL, 0);
        const rawRemaining = totalAmount - totalPaid;
        const remaining = Math.abs(rawRemaining) < 0.01 ? 0 : rawRemaining;
        const currentAmountTL = selectedPaymentCurrency === 'TRY'
          ? Number(paymentAmount) || 0
          : (Number(paymentAmount) || 0) * getPaymentExchangeRate(selectedPaymentCurrency);

        return (
          <div className={`modal-overlay ${closingModal === 'payment' ? 'closing' : ''}`}>
            <div className="modal cash-modal" onClick={e => e.stopPropagation()}>
              <div className="modal-header">
                <h2>Müşteriye Ödeme</h2>
                <button className="close-btn" onClick={() => handleCloseModal('payment', setShowPaymentModal)}>✕</button>
              </div>
              <div className="modal-body">
                {/* Özet Bilgisi - Yan Yana */}
                <div style={{ display: 'flex', gap: '8px', marginBottom: '10px', background: '#f8f9fa', borderRadius: '8px', padding: '8px 10px' }}>
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: '2px' }}>Toplam Tutar</div>
                    {isEditingTotal ? (
                      <PriceInput
                        style={{ width: '90%', fontSize: '0.88rem', fontWeight: 700, textAlign: 'center', border: '1px solid #00bfa6', borderRadius: '4px', padding: '2px', outline: 'none' }}
                        value={editingTotalValue}
                        onChange={(val) => setEditingTotalValue(val)}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            const newValue = parseFloat(editingTotalValue);
                            if (!isNaN(newValue) && newValue >= 0) {
                              setPendingScrapData(prev => ({ ...prev, totalAmount: newValue }));
                            }
                            setIsEditingTotal(false);
                          }
                          if (e.key === 'Escape') setIsEditingTotal(false);
                        }}
                        autoFocus
                      />
                    ) : (
                      <div
                        style={{ fontSize: '0.88rem', fontWeight: 700, color: '#1e293b', cursor: 'pointer' }}
                        onDoubleClick={() => { setEditingTotalValue(totalAmount.toFixed(2)); setIsEditingTotal(true); }}
                        title="Çift tıkla düzenle"
                      >₺{totalAmount.toFixed(2)}</div>
                    )}
                  </div>
                  {totalPaid > 0 && <>
                    <div style={{ width: '1px', background: '#e2e8f0' }} />
                    <div style={{ flex: 1, textAlign: 'center' }}>
                      <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: '2px' }}>Toplam Ödenen</div>
                      <div style={{ fontSize: '0.88rem', fontWeight: 700, color: '#16a34a' }}>₺{totalPaid.toFixed(2)}</div>
                    </div>
                  </>}
                  <div style={{ width: '1px', background: '#e2e8f0' }} />
                  <div style={{ flex: 1, textAlign: 'center' }}>
                    <div style={{ fontSize: '0.68rem', color: '#64748b', marginBottom: '2px' }}>{remaining > 0 ? 'Kalan Tutar' : 'Tamamlandı'}</div>
                    <div style={{ fontSize: '0.88rem', fontWeight: 700, color: remaining > 0 ? '#dc2626' : '#16a34a' }}>{remaining > 0 ? `₺${remaining.toFixed(2)}` : '✓'}</div>
                  </div>
                </div>

                {/* Yapılan Ödemeler - Kompakt Chip */}
                {scrapPayments.length > 0 && (
                  <div style={{ marginBottom: '8px', display: 'flex', flexWrap: 'wrap', gap: '6px', alignItems: 'center' }}>
                    {scrapPayments.map(payment => (
                      <div key={payment.id} style={{ display: 'inline-flex', alignItems: 'center', gap: '5px', padding: '3px 10px', background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: '16px', fontSize: '0.78rem' }}>
                        <span style={{ fontWeight: 600, color: '#334155' }}>
                          {payment.method}{payment.bankAccountName ? ` (${payment.bankAccountName.substring(0, 8)})` : ''}
                        </span>
                        <span style={{ color: '#16a34a', fontWeight: 700 }}>
                          {payment.currency !== 'TRY' ? (
                            <>{payment.currency === 'USD' ? '$' : '€'}{payment.amount.toFixed(2)}</>
                          ) : (
                            <>₺{payment.amountTL.toFixed(2)}</>
                          )}
                        </span>
                        <button onClick={() => removeScrapPayment(payment.id)} style={{ background: 'none', border: 'none', color: '#94a3b8', cursor: 'pointer', padding: '0 0 0 2px', fontSize: '13px', lineHeight: 1 }}>✕</button>
                      </div>
                    ))}
                    <span style={{ fontSize: '0.75rem', color: '#64748b', marginLeft: '4px' }}>
                      Toplam: <strong style={{ color: '#16a34a' }}>₺{totalPaid.toLocaleString('tr-TR', {minimumFractionDigits: 2})}</strong>
                    </span>
                  </div>
                )}

                {/* Yeni Ödeme Ekleme - Sadece kalan varsa göster */}
                {remaining > 0 && (
                  <>
                    {/* Döviz Seçimi */}
                    <div style={{ display: 'flex', background: '#f1f5f9', borderRadius: '6px', padding: '2px', marginBottom: '10px', gap: '2px' }}>
                      {[
                        { key: 'TRY', label: '₺ TL', rate: null },
                        { key: 'USD', label: '$ USD', rate: `1$=₺${getPaymentExchangeRate('USD').toLocaleString('tr-TR')}` },
                        { key: 'EUR', label: '€ EUR', rate: `1€=₺${getPaymentExchangeRate('EUR').toLocaleString('tr-TR')}` }
                      ].map(c => (
                        <button
                          key={c.key}
                          onClick={() => { setSelectedPaymentCurrency(c.key); setPaymentAmount(''); }}
                          style={{
                            flex: 1, padding: '7px 8px', border: 'none', borderRadius: '5px', cursor: 'pointer',
                            background: selectedPaymentCurrency === c.key ? '#fff' : 'transparent',
                            color: selectedPaymentCurrency === c.key ? '#00897b' : '#64748b',
                            fontWeight: selectedPaymentCurrency === c.key ? 600 : 400,
                            fontSize: '0.65rem', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '3px',
                            boxShadow: selectedPaymentCurrency === c.key ? '0 1px 2px rgba(0,0,0,0.08)' : 'none',
                            transition: 'all 0.15s'
                          }}
                        >
                          <span style={{ fontSize: '0.85rem', fontWeight: 600 }}>{c.label}</span>
                          {c.rate && <span style={{ fontSize: '0.65rem', opacity: 0.6 }}>{c.rate}</span>}
                        </button>
                      ))}
                    </div>

                    {/* Tutar Girişi */}
                    <div className="cash-input-section">
                      <label>
                        Tutar ({selectedPaymentCurrency === 'TRY' ? '₺' : selectedPaymentCurrency === 'USD' ? '$' : '€'})
                      </label>
                      <PriceInput
                        className="cash-input"
                        value={paymentAmount}
                        onChange={(val) => setPaymentAmount(val)}
                        placeholder="0.00"
                        autoFocus
                        onKeyDown={(e) => {
                          if (e.key === 'Tab' && !paymentAmount && remaining > 0) {
                            e.preventDefault();
                            if (selectedPaymentCurrency === 'TRY') {
                              setPaymentAmount(remaining.toFixed(2));
                            } else {
                              const rate = getPaymentExchangeRate(selectedPaymentCurrency);
                              setPaymentAmount(rate > 0 ? (remaining / rate).toFixed(2) : '0');
                            }
                          }
                        }}
                      />
                      <span style={{ fontSize: '0.68rem', color: '#94a3b8', marginTop: '3px', display: 'flex', alignItems: 'center', gap: '3px' }}><Lightbulb size={11} color="#00bfa6" /> Tab ile kalan tutarı doldur</span>
                    </div>

                    {/* Döviz ise TL karşılığını göster */}
                    {selectedPaymentCurrency !== 'TRY' && paymentAmount && Number(paymentAmount) > 0 && (
                      <div className="tl-equivalent">
                        <span className="tl-label">TL Karşılığı:</span>
                        <span className="tl-amount">₺{currentAmountTL.toLocaleString('tr-TR', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</span>
                      </div>
                    )}

                    {/* Ödeme Yöntemi Butonları */}
                    <div className="payment-method-buttons">
                      <button
                        className="method-btn cash"
                        onClick={() => { addScrapPayment('Nakit'); setShowPaymentCreditOptions(false); }}
                        disabled={!paymentAmount || currentAmountTL <= 0}
                      >
                        <Banknote size={16} /> Nakit
                      </button>
                      <button
                        className="method-btn transfer"
                        onClick={() => {
                          if (!paymentAmount || currentAmountTL <= 0) {
                            toast.warning('Önce tutar girin!');
                            return;
                          }
                          setShowPaymentCreditOptions(false);
                          setShowBankModal(true);
                        }}
                        disabled={!paymentAmount || currentAmountTL <= 0}
                      >
                        <Building2 size={16} /> Havale
                      </button>
                      <button
                        className={`method-btn credit ${showPaymentCreditOptions ? 'active' : ''}`}
                        onClick={() => setShowPaymentCreditOptions(!showPaymentCreditOptions)}
                        disabled={!paymentAmount || currentAmountTL <= 0}
                      >
                        <ClipboardList size={16} /> Açık Hesap
                      </button>
                    </div>

                    {/* Açık Hesap Seçenekleri */}
                    {showPaymentCreditOptions && paymentAmount && currentAmountTL > 0 && (
                      <div className="credit-options">
                        <div className="credit-option" onClick={() => {
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (TL)',
                            currency: 'TRY',
                            amount: currentAmountTL,
                            amountTL: currentAmountTL,
                            creditType: 'TL',
                            rate: 1
                          };
                          setScrapPayments([...scrapPayments, newPayment]);
                          setPaymentAmount('');
                          setShowPaymentCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">₺</span>
                          <div className="credit-option-info">
                            <span className="credit-option-title">TL Olarak Kaydet</span>
                            <span className="credit-option-value">₺{currentAmountTL.toFixed(2)} borç</span>
                          </div>
                        </div>
                        <div className="credit-option" onClick={() => {
                          const goldPrice = getPaymentExchangeRate('GOLD');
                          const goldGram = goldPrice > 0 ? (currentAmountTL / goldPrice).toFixed(3) : 0;
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (Gram)',
                            currency: 'GOLD',
                            amount: parseFloat(goldGram),
                            amountTL: currentAmountTL,
                            creditType: 'GRAM',
                            goldRate: goldPrice,
                            rate: goldPrice
                          };
                          setScrapPayments([...scrapPayments, newPayment]);
                          setPaymentAmount('');
                          setShowPaymentCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">Au</span>
                          <div className="credit-option-info">
                            <span className="credit-option-title">Gram Altın Olarak Kaydet</span>
                            <span className="credit-option-value">
                              {(() => {
                                const goldPrice = getPaymentExchangeRate('GOLD');
                                return goldPrice > 0 ? (currentAmountTL / goldPrice).toFixed(3) : '?';
                              })()} gram borç
                              <small> (Gram: ₺{getPaymentExchangeRate('GOLD').toLocaleString('tr-TR')})</small>
                            </span>
                          </div>
                        </div>
                        <div className="credit-option silver" onClick={() => {
                          const silverPrice = getPaymentExchangeRate('SILVER');
                          const silverGram = silverPrice > 0 ? (currentAmountTL / silverPrice).toFixed(2) : 0;
                          const newPayment = {
                            id: Date.now(),
                            method: 'Açık Hesap (Gümüş)',
                            currency: 'SILVER',
                            amount: parseFloat(silverGram),
                            amountTL: currentAmountTL,
                            creditType: 'GUMUS',
                            silverRate: silverPrice,
                            rate: silverPrice
                          };
                          setScrapPayments([...scrapPayments, newPayment]);
                          setPaymentAmount('');
                          setShowPaymentCreditOptions(false);
                        }}>
                          <span className="credit-option-icon">Ag</span>
                          <div className="credit-option-info">
                            <span className="credit-option-title">Gram Gümüş Olarak Kaydet</span>
                            <span className="credit-option-value">
                              {(() => {
                                const silverPrice = getPaymentExchangeRate('SILVER');
                                return silverPrice > 0 ? (currentAmountTL / silverPrice).toFixed(2) : '?';
                              })()} gram borç
                              <small> (Gram: ₺{getPaymentExchangeRate('SILVER').toLocaleString('tr-TR')})</small>
                            </span>
                          </div>
                        </div>
                      </div>
                    )}
                  </>
                )}
              </div>
              <div className="modal-footer">
                <button className="btn-cancel" onClick={() => {
                  setShowPaymentModal(false);
                  setScrapPayments([]);
                  setPendingScrapData(null);
                }}>İptal</button>
                <button
                  className="btn-save"
                  onClick={completeScrapPurchase}
                  disabled={remaining > 0}
                >
                  {remaining <= 0 ? 'Ödemeyi Tamamla' : `Kalan: ₺${remaining.toFixed(2)}`}
                </button>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Banka Seçim Modalı */}
      {showBankModal && (
        <div className={`modal-overlay ${closingModal === 'bank' ? 'closing' : ''}`}>
          <div className="bank-select-modal" onClick={(e) => e.stopPropagation()}>
            <div className="modal-header">
              <h2><Building2 size={22} /> Banka Hesabı Seç</h2>
              <button className="close-btn" onClick={() => handleCloseModal('bank', setShowBankModal)}>
                <X size={24} />
              </button>
            </div>
            <div className="modal-body">
              <div className="bank-accounts-list">
                {bankAccounts.length === 0 ? (
                  <p className="no-banks">Aktif banka hesabı bulunamadı.</p>
                ) : (
                  bankAccounts.map(bank => (
                    <button
                      key={bank.id}
                      className="bank-account-btn"
                      onClick={() => handleBankSelect(bank)}
                    >
                      <div className="bank-info">
                        <strong>{bank.bank_name}</strong>
                        <span>{bank.account_name}</span>
                        <span className="iban">{bank.iban}</span>
                      </div>
                    </button>
                  ))
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Grup Detay Modalı */}
      {showDetailModal && selectedGroupDetail && (
        <div className={`modal-overlay ${closingModal === 'detail' ? 'closing' : ''}`}>
          <div className="modal detail-modal" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px', width: '95%' }}>
            <div className="modal-header">
              <h2><Scale size={20} style={{ marginRight: '8px' }} /> Hurda Alım Fişi #{selectedGroupDetail.store_scrap_number || selectedGroupDetail.first_id}</h2>
              <button className="close-btn" onClick={() => handleCloseModal('detail', setShowDetailModal)}>✕</button>
            </div>
            <div className="modal-body detail-modal-body">
              {/* Fiş Özeti */}
              <div className="detail-section">
                <h3><User size={16} /> Fiş Bilgileri</h3>
                <div className="detail-grid">
                  <div className="detail-item">
                    <span className="detail-label">Müşteri Adı</span>
                    <span className="detail-value">{selectedGroupDetail.customer_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Telefon</span>
                    <span className="detail-value">{selectedGroupDetail.customer_phone || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Personel</span>
                    <span className="detail-value">{selectedGroupDetail.staff_name || '-'}</span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Tarih</span>
                    <span className="detail-value">
                      {new Date(selectedGroupDetail.created_at).toLocaleDateString('tr-TR')} {new Date(selectedGroupDetail.created_at).toLocaleTimeString('tr-TR', { hour: '2-digit', minute: '2-digit' })}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Ödeme Yöntemi</span>
                    <span className="detail-value">
                      {selectedGroupDetail.payment_method || '-'}
                      {(() => {
                        // Havale ödemesi varsa banka hesap bilgisini göster
                        if (selectedGroupDetail.payment_method === 'Havale' || selectedGroupDetail.payment_method === 'Karışık') {
                          try {
                            const firstItem = groupDetailItems[0];
                            const details = firstItem?.payment_details
                              ? (typeof firstItem.payment_details === 'string' ? JSON.parse(firstItem.payment_details) : firstItem.payment_details)
                              : [];
                            const havalePayment = Array.isArray(details) ? details.find(p => p.method === 'Havale' && p.bankAccountName) : null;
                            if (havalePayment?.bankAccountName) {
                              return <span style={{ color: '#6366f1', marginLeft: '6px' }}>({havalePayment.bankAccountName})</span>;
                            }
                          } catch(e) {}
                        }
                        return null;
                      })()}
                    </span>
                  </div>
                  <div className="detail-item">
                    <span className="detail-label">Ürün Sayısı</span>
                    <span className="detail-value">{selectedGroupDetail.item_count} adet</span>
                  </div>
                  {/* Satıştan gelen hurda ise satış bilgisi */}
                  {(selectedGroupDetail.sale_id || (groupDetailItems.length > 0 && groupDetailItems[0].sale_id)) && (
                    <div className="detail-item">
                      <span className="detail-label">Bağlı Satış</span>
                      <span className="detail-value" style={{ color: '#2563eb', fontWeight: 600 }}>
                        Fiş #{groupDetailItems.length > 0 && groupDetailItems[0].sale_receipt_number
                          ? groupDetailItems[0].sale_receipt_number
                          : (selectedGroupDetail.sale_id || groupDetailItems[0]?.sale_id)}
                      </span>
                    </div>
                  )}
                </div>
                {/* Ödeme Detayları */}
                {groupDetailItems.length > 0 && (() => {
                  const firstItem = groupDetailItems[0];
                  let payments = [];
                  try {
                    payments = firstItem.payment_details
                      ? (typeof firstItem.payment_details === 'string' ? JSON.parse(firstItem.payment_details) : firstItem.payment_details)
                      : [];
                  } catch(e) { payments = []; }
                  if (payments.length === 0) return null;
                  return (
                    <div style={{ marginTop: '12px', padding: '10px 14px', background: 'rgba(0,0,0,0.03)', borderRadius: '8px' }}>
                      <span style={{ fontSize: '0.8rem', fontWeight: 600, color: '#64748b', marginBottom: '6px', display: 'block' }}>Ödeme Detayları</span>
                      {payments.map((p, i) => (
                        <div key={i} style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '4px 0', fontSize: '0.85rem' }}>
                          <span style={{ color: '#334155' }}>
                            {p.method}
                            {p.bankAccountName && <span style={{ color: '#6366f1', marginLeft: '6px' }}>({p.bankAccountName})</span>}
                            {p.creditType && <span style={{ color: '#ea580c', marginLeft: '6px' }}>({p.creditType})</span>}
                            {p.currency && p.currency !== 'TRY' && <span style={{ color: '#0891b2', marginLeft: '6px' }}>({p.currency})</span>}
                          </span>
                          <span style={{ fontWeight: 600, color: '#0f172a' }}>
                            {p.currency && p.currency !== 'TRY'
                              ? `${Number(p.amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })} ${p.currency} (₺${Number(p.amountTL || p.amount_tl || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })})`
                              : `₺${Number(p.amountTL || p.amount_tl || p.amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}`
                            }
                          </span>
                        </div>
                      ))}
                    </div>
                  );
                })()}
              </div>

              {/* Ürün Listesi */}
              <div className="detail-section">
                <h3><Coins size={16} /> Alınan Ürünler</h3>
                <div className="group-items-table">
                  <table className="sp-items-table" style={{ width: '100%', borderCollapse: 'collapse' }}>
                    <thead>
                      <tr style={{ backgroundColor: 'rgba(0,0,0,0.05)', borderBottom: '2px solid #ddd' }}>
                        <th style={{ padding: '10px', textAlign: 'left' }}>#</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Metal</th>
                        <th style={{ padding: '10px', textAlign: 'left' }}>Ayar</th>
                        <th style={{ padding: '10px', textAlign: 'right' }}>Brüt (gr)</th>
                        <th style={{ padding: '10px', textAlign: 'right' }}>Fire (gr)</th>
                        <th style={{ padding: '10px', textAlign: 'right' }}>Net (gr)</th>
                        <th style={{ padding: '10px', textAlign: 'right' }}>Has (gr)</th>
                        <th style={{ padding: '10px', textAlign: 'right' }}>Birim Fiyat</th>
                        <th style={{ padding: '10px', textAlign: 'right' }}>Tutar</th>
                      </tr>
                    </thead>
                    <tbody>
                      {groupDetailItems.map((item, idx) => {
                        const grossW = Number(item.gross_weight) || 0;
                        const storedFire = Number(item.waste_gram) || 0;
                        const wasteP = Number(item.waste_percent) || 0;
                        const fire = storedFire > 0 ? storedFire : (wasteP > 0 ? grossW * wasteP / 100 : 0);
                        const netW = grossW - fire;
                        return (
                        <tr key={item.id} style={{ borderBottom: '1px solid #eee' }}>
                          <td style={{ padding: '10px' }}>{idx + 1}</td>
                          <td style={{ padding: '10px' }}>
                            {item.metal_type === 'gold' ? (
                              <span className="metal-badge gold">Altın</span>
                            ) : (
                              <span className="metal-badge silver">Gümüş</span>
                            )}
                          </td>
                          <td style={{ padding: '10px' }}>
                            {item.purity} Ayar
                            {(() => {
                              const rate = purityRates[item.metal_type]?.[item.purity] ?? defaultPurityRates[item.metal_type]?.[item.purity];
                              return rate ? ` (${Math.round(rate * 1000)} Milyem)` : '';
                            })()}
                          </td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{grossW.toFixed(2)}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>{fire.toFixed(2)}</td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>{netW.toFixed(2)}</td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>{Number(item.pure_weight).toFixed(2)}</td>
                          <td style={{ padding: '10px', textAlign: 'right' }}>₺{Number(item.unit_price || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                          <td style={{ padding: '10px', textAlign: 'right', fontWeight: 'bold' }}>
                            ₺{Number(item.total_price || item.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                            {item.calculated_amount != null && Math.abs(Number(item.calculated_amount) - Number(item.total_price || item.total_amount || 0)) > 0.01 && (
                              <div style={{ fontWeight: 400, fontSize: '0.72rem', color: '#94a3b8', marginTop: '2px' }}>
                                Hesaplanan: ₺{Number(item.calculated_amount).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                              </div>
                            )}
                          </td>
                        </tr>
                        );
                      })}
                    </tbody>
                    <tfoot>
                      {(() => {
                        const totalFire = groupDetailItems.reduce((sum, i) => {
                          const g = Number(i.gross_weight) || 0;
                          const wg = Number(i.waste_gram) || 0;
                          const wp = Number(i.waste_percent) || 0;
                          return sum + (wg > 0 ? wg : (wp > 0 ? g * wp / 100 : 0));
                        }, 0);
                        const totalGross = Number(selectedGroupDetail.total_gross_weight || 0);
                        return (
                      <tr style={{ backgroundColor: 'rgba(20, 184, 166, 0.1)', fontWeight: 'bold', borderTop: '2px solid #14b8a6' }}>
                        <td colSpan="3" style={{ padding: '12px', textAlign: 'left' }}>TOPLAM</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{totalGross.toFixed(2)} gr</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{totalFire.toFixed(2)} gr</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontWeight: '600', color: '#16a34a' }}>{(totalGross - totalFire).toFixed(2)} gr</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>{Number(selectedGroupDetail.total_pure_weight || 0).toFixed(2)} gr</td>
                        <td style={{ padding: '12px', textAlign: 'right' }}>-</td>
                        <td style={{ padding: '12px', textAlign: 'right', fontSize: '1.1em', color: '#14b8a6' }}>₺{Number(selectedGroupDetail.total_amount || 0).toLocaleString('tr-TR', { minimumFractionDigits: 2 })}</td>
                      </tr>
                        );
                      })()}
                    </tfoot>
                  </table>
                </div>
              </div>
            </div>
            <div className="modal-footer">
              <button className="btn-cancel" onClick={() => setShowDetailModal(false)}>Kapat</button>
              {user?.role === 'admin' && groupDetailItems.length === 1 && (
                <button className="btn-edit" onClick={() => { setShowDetailModal(false); openEditModal(groupDetailItems[0]); }}>
                  <Pencil size={16} /> Düzenle
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* Log Modal */}
      <LogModal
        isOpen={showLogModal}
        onClose={() => setShowLogModal(false)}
        module="scrap"
        title="Hurda Alımı"
        showEntityCode={false}
      />

      {/* Genel Onay Modal (window.confirm yerine) */}
      {showConfirmModal && (
        <div className="modal-overlay" style={{ zIndex: 10000 }}>
          <div className="modal confirm-modal" onClick={e => e.stopPropagation()} style={{
            maxWidth: '420px',
            borderRadius: '16px',
            overflow: 'hidden',
            boxShadow: '0 20px 60px rgba(0,0,0,0.3)'
          }}>
            <div className="modal-header" style={{
              background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                         confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                         'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
              color: 'white',
              padding: '20px 24px',
              display: 'flex',
              alignItems: 'center',
              gap: '12px'
            }}>
              <AlertTriangle size={28} />
              <h2 style={{ margin: 0, fontSize: '18px', fontWeight: '600' }}>{confirmModalConfig.title}</h2>
            </div>
            <div className="modal-body" style={{ padding: '24px', background: '#fff' }}>
              <p style={{ margin: 0, fontSize: '15px', color: '#374151', lineHeight: '1.6' }}>
                {confirmModalConfig.message}
              </p>
            </div>
            <div className="modal-footer" style={{
              padding: '16px 24px',
              background: '#f9fafb',
              display: 'flex',
              justifyContent: 'flex-end',
              gap: '12px',
              borderTop: '1px solid #e5e7eb'
            }}>
              <button
                onClick={() => setShowConfirmModal(false)}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: '1px solid #d1d5db',
                  background: '#fff',
                  color: '#374151',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.cancelText}
              </button>
              <button
                onClick={() => {
                  setShowConfirmModal(false);
                  confirmModalConfig.onConfirm();
                }}
                style={{
                  padding: '10px 20px',
                  borderRadius: '8px',
                  border: 'none',
                  background: confirmModalConfig.type === 'danger' ? 'linear-gradient(135deg, #ef4444 0%, #dc2626 100%)' :
                             confirmModalConfig.type === 'info' ? 'linear-gradient(135deg, #3b82f6 0%, #2563eb 100%)' :
                             'linear-gradient(135deg, #f59e0b 0%, #d97706 100%)',
                  color: 'white',
                  fontSize: '14px',
                  fontWeight: '500',
                  cursor: 'pointer',
                  transition: 'all 0.2s'
                }}
              >
                {confirmModalConfig.confirmText}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ScrapPurchase;
